"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter, useSearchParams, usePathname } from "next/navigation"
import { useTranslation } from "@/i18n/client"
import { cn } from "@/lib/util"
import PlanCard from "./PlanCard"
import { trackYohoEvent } from "@/lib/analytics/track"
import {
  API_BASE,
  SMART_API_BASE,
  deduplicatePlans,
  formatPrice,
  formatQuota,
  getLowestPrice,
  getFlagUrl,
  buildCountriesParam,
  getInventorySearchParams,
  resolveDualEngine,
  getDynamicQuotaOptions,
  getProbeSearchParams,
  getDynamicDailyOptions,
  normalizePlans,
  snapQuotaForAPI,
} from "./helpers"
import type { RecommendationPlan, DualEngineResult, DualEngineOffer } from "./types"

// =============================================================================
// Types
// =============================================================================

interface CountryEntry {
  name: string
  /** English name — used for cross-language search */
  nameEN: string
  iso2: string
}

interface TripConfiguratorProps {
  baseIso2s: string[]
  baseCountries: string
  countryName: string
  lng: string
  initialPlans: RecommendationPlan[]
  initialCacheKey?: string
  /** Flat list of single-country entries for the country picker */
  availableCountries: CountryEntry[]
  /** Pre-computed total-data options from server-side probe (avoids first-load flicker) */
  initialTotalOptions?: Array<{ label: string; value: number }>
  /** Pre-computed daily-data options from server-side probe */
  initialDailyOptions?: Array<{ label: string; labelEN: string; value: number }>
  /** Region-aware quick preset chips for the country selector */
  countryPresets?: Array<{ label: string; iso2s: string[]; href?: string }>
}

// =============================================================================
// Constants
// =============================================================================

const DAY_PRESETS = [3, 5, 7, 10, 15, 30]
const MIN_DAYS = 1
const MAX_DAYS = 365
const SLIDER_MAX = 30
const RANGE_SLIDER_CLASS_NAME = "relative z-10 h-8 w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-home-orange_1 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:mt-[-6px] [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-home-orange_1 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md"

type DataMode = "total" | "daily" | "unlimited"

type SmartCategories = {
  single_country: RecommendationPlan[]
  multi_country: RecommendationPlan[]
  upgrades: RecommendationPlan[]
}

type ResultsSnapshot = {
  totalOffers: number
  dualResult: DualEngineResult
  smartCategories: SmartCategories | null
  iso2s: string[]
  countriesForBuyUrl: string
  selectedPlanKey: string | null
}

type SmartSlot = "single" | "multi" | "upgrade" | "phone"

type SmartRecommendationOffer = {
  plan: RecommendationPlan
  slot: SmartSlot
}

// ─── Quota-tier slot system (web V3) ────────────────────────────────────────
// Replaces the legacy "single / multi / upgrade" slot picker with a 4-tier
// stepper based on the user's chosen data quota. Every tier has a clear role:
//   saver     — less data than chosen, cheaper (only when meaningful)
//   fit       — closest match to chosen quota (the anchor)
//   more      — more data than chosen, modest price bump
//   unlimited — unlimited, top spec
// At most 4 cards are rendered; tiers without a viable plan are skipped.
type QuotaTier = "saver" | "fit" | "more" | "unlimited"

type QuotaTierOffer = {
  plan: RecommendationPlan
  tier: QuotaTier
  /** Trip-total MB (daily quota * days, or total quota); Infinity for unlimited. */
  tripQuotaMB: number
}

/** Trip-total MB for a plan (daily plans multiply by their day count). */
function getPlanTripQuotaMB(plan: RecommendationPlan): number {
  const q = plan.data.quota
  if (q === -1) return Number.POSITIVE_INFINITY
  if (typeof q !== "number" || !isFinite(q) || q <= 0) return 0
  return plan.data.daily ? q * Math.max(1, plan.data.day || 1) : q
}

/**
 * Pick at most 4 plans bucketed into quota tiers (saver / fit / more / unlimited).
 * Each call works on a pre-filtered candidate pool that already covers the
 * user's iso2s. Tiers without a candidate are dropped from the output.
 */
function pickQuotaTierOffers({
  candidates,
  userTripQuotaMB,
}: {
  candidates: RecommendationPlan[]
  /** Trip-total user quota in MB. Pass Infinity if user picked unlimited. */
  userTripQuotaMB: number
}): QuotaTierOffer[] {
  if (candidates.length === 0) return []

  const userPickedUnlimited = !isFinite(userTripQuotaMB)

  // Decorate each candidate with computed trip quota for cheap filtering.
  const decorated = candidates
    .map((plan) => ({ plan, tripQuotaMB: getPlanTripQuotaMB(plan) }))
    .filter((d) => d.plan.amount > 0 && (d.tripQuotaMB > 0 || d.tripQuotaMB === Number.POSITIVE_INFINITY))

  const cheapest = (arr: typeof decorated) =>
    arr.length === 0
      ? null
      : [...arr].sort((a, b) => a.plan.amount - b.plan.amount)[0]

  const used = new Set<string>()
  const take = (
    d: { plan: RecommendationPlan; tripQuotaMB: number } | null,
    tier: QuotaTier
  ): QuotaTierOffer | null => {
    if (!d) return null
    const key = getPlanRenderKey(d.plan)
    if (used.has(key)) return null
    used.add(key)
    return { plan: d.plan, tier, tripQuotaMB: d.tripQuotaMB }
  }

  // Fit tier: closest to user quota and cheapest within ±35% range.
  let fitOffer: QuotaTierOffer | null = null
  if (userPickedUnlimited) {
    fitOffer = take(cheapest(decorated.filter((d) => d.tripQuotaMB === Number.POSITIVE_INFINITY)), "fit")
  } else {
    const lo = userTripQuotaMB * 0.65
    const hi = userTripQuotaMB * 1.35
    const inRange = decorated.filter((d) => d.tripQuotaMB >= lo && d.tripQuotaMB <= hi)
    fitOffer = take(cheapest(inRange), "fit")
    // If no plan in range, fall back to the plan whose quota is closest in absolute terms.
    if (!fitOffer) {
      const finite = decorated.filter((d) => isFinite(d.tripQuotaMB))
      if (finite.length > 0) {
        const closest = [...finite].sort(
          (a, b) =>
            Math.abs(a.tripQuotaMB - userTripQuotaMB) -
            Math.abs(b.tripQuotaMB - userTripQuotaMB)
        )[0]
        fitOffer = take(closest, "fit")
      }
    }
  }

  // Saver tier: less data, cheaper. Only meaningful if user chose >= 3GB.
  // Use the fit plan's tripQuota as the upper bound (must be strictly less).
  let saverOffer: QuotaTierOffer | null = null
  if (!userPickedUnlimited && userTripQuotaMB >= 3 * 1024 && fitOffer) {
    const fitQ = fitOffer.tripQuotaMB
    const saverPool = decorated.filter(
      (d) => isFinite(d.tripQuotaMB) && d.tripQuotaMB < fitQ * 0.85 && d.plan.amount < fitOffer!.plan.amount
    )
    saverOffer = take(cheapest(saverPool), "saver")
  }

  // More tier: more data than fit, but bounded (no unlimited; no >5x for sanity).
  let moreOffer: QuotaTierOffer | null = null
  if (!userPickedUnlimited && fitOffer) {
    const fitQ = fitOffer.tripQuotaMB
    const morePool = decorated.filter(
      (d) =>
        isFinite(d.tripQuotaMB) &&
        d.tripQuotaMB >= fitQ * 1.4 &&
        d.tripQuotaMB <= fitQ * 5
    )
    moreOffer = take(cheapest(morePool), "more")
  }

  // Unlimited tier: only when user did NOT already pick unlimited.
  let unlimitedOffer: QuotaTierOffer | null = null
  if (!userPickedUnlimited) {
    const unlimitedPool = decorated.filter((d) => d.tripQuotaMB === Number.POSITIVE_INFINITY)
    unlimitedOffer = take(cheapest(unlimitedPool), "unlimited")
  }

  // Output order: saver → fit → more → unlimited (price/quota ascending).
  return [saverOffer, fitOffer, moreOffer, unlimitedOffer].filter(
    (o): o is QuotaTierOffer => o !== null
  )
}

function getPlanRenderKey(plan: RecommendationPlan): string {
  const supportCountries = (plan.data.support_countries || [])
    .map((country) => String(country).toUpperCase())
    .sort()
    .join("_")
  return `${plan.provider}_${plan.data.quota}_${plan.data.day}_${plan.data.daily}_${plan.amount}_${supportCountries}`
}

function getSmartPlanCountries(plan: RecommendationPlan): string[] {
  return (plan.data.support_countries || []).map((country) => String(country).toUpperCase())
}

function isUnlimitedSmartPlan(plan: RecommendationPlan): boolean {
  const quota = plan.data.quota as unknown
  return quota === -1 || String(quota).toLowerCase() === "unlimited"
}

function isJoytelPlan(plan: RecommendationPlan): boolean {
  const provider = String(plan.provider || "").toUpperCase()
  return provider === "J" || provider === "JOYTEL"
}

function dedupeSmartPlans(plans: RecommendationPlan[]): RecommendationPlan[] {
  const uniquePlans = new Map<string, RecommendationPlan>()
  for (const plan of plans) {
    uniquePlans.set(getPlanRenderKey(plan), plan)
  }
  return Array.from(uniquePlans.values())
}

/**
 * Single-country flat-list helpers. The count shown in the page header
 * and the count actually rendered MUST come from the same algorithm —
 * previously the header used pickSmartRecommendationSlots (≤4 tier slots)
 * while the grid rendered tightDedupe + capability-fingerprint output
 * (often 4–9 cards). That mismatch told users "2 plans for you" while
 * showing them 9 cards.
 */
function tightDedupeFlatPlans(plans: RecommendationPlan[]): RecommendationPlan[] {
  const byKey = new Map<string, RecommendationPlan>()
  for (const plan of plans) {
    const key = `${plan.provider}_${plan.data.quota}_${plan.data.day}_${plan.data.daily}_${plan.amount}`
    const prev = byKey.get(key)
    const cur = (plan.data.support_countries || []).length
    const prevCount = prev ? (prev.data.support_countries || []).length : Infinity
    if (!prev || cur < prevCount) byKey.set(key, plan)
  }
  return Array.from(byKey.values())
}

/**
 * 套餐唯一身份指纹：运营商集合（按字母排序）+ feature 集合（phoneNumber/hotspot/topup/yohoCare/daily）。
 * 用于跟 Flutter app 对齐推荐展示 —— 同指纹只保留最便宜的一张，避免同能力多 provider 各显一张造成"卡片爆炸"。
 *
 * 示例（韩国）：
 *   Telna 10GB/7天 KT,SKT (基础)  → "KT,SK Telecom__T"
 *   CMLink 10GB/7天 KT,SKT (yoho care + hotspot + topup)  → "KT,SK Telecom__h|t|y"
 *   Joytel 10GB/7天 KT,SKT,LG U+ (yoho care + hotspot + topup) → "KT,LG U+,SK Telecom__h|t|y"
 * 三个指纹不同，全保留；同指纹的多 provider 则只保留最便宜的。
 */
function getPlanFingerprint(plan: RecommendationPlan, userIso: string): string {
  const operators = (plan.data.network?.[userIso] || [])
    .map((op) => op.operator?.trim())
    .filter((v): v is string => !!v)
    .sort()
    .join(",")
  // 运营商列表为空时（数据缺失）退化为 provider，避免不同套餐被错误合并。
  const operatorKey = operators || `provider:${plan.provider}`

  const tags = (plan.tags || {}) as {
    phoneNumber?: boolean
    hotspot?: boolean
    topup?: boolean
    yohoCare?: boolean
  }
  // CMLink 系列 provider 默认带 yoho care（与 dedupeFlatUnlimitedByCapability 保持一致）。
  const hasYohoCare =
    tags.yohoCare === true || plan.provider === "CMLink" || plan.provider === "C"
  const features = [
    tags.phoneNumber ? "p" : "",
    tags.hotspot ? "h" : "",
    tags.topup ? "t" : "",
    hasYohoCare ? "y" : "",
    plan.data.daily ? "D" : "T",
  ]
    .filter(Boolean)
    .join("|")

  return `${operatorKey}__${features}`
}

/**
 * 按指纹去重，每组留最便宜的一张。返回所有 finite plans。
 * 调用方负责对 unlimited 单独处理（不要混入此函数）。
 */
function dedupeFlatByFingerprint(
  plans: RecommendationPlan[],
  userIso: string
): RecommendationPlan[] {
  const byFp = new Map<string, RecommendationPlan>()
  for (const plan of plans) {
    const fp = getPlanFingerprint(plan, userIso)
    const prev = byFp.get(fp)
    if (!prev || plan.amount < prev.amount) byFp.set(fp, plan)
  }
  return Array.from(byFp.values()).sort((a, b) => a.amount - b.amount)
}

function dedupeFlatUnlimitedByCapability(plans: RecommendationPlan[]): RecommendationPlan[] {
  const byKey = new Map<string, RecommendationPlan>()
  for (const plan of plans) {
    const tg = plan.tags
    const hasYohoCare =
      tg?.yohoCare === true || plan.provider === "CMLink" || plan.provider === "C"
    const fp = [
      tg?.phoneNumber ? "p" : "",
      tg?.hotspot ? "h" : "",
      tg?.topup ? "t" : "",
      hasYohoCare ? "y" : "",
      plan.data.daily ? "D" : "T",
    ].join("")
    const prev = byKey.get(fp)
    if (!prev || plan.amount < prev.amount) byKey.set(fp, plan)
  }
  return Array.from(byKey.values()).sort((a, b) => a.amount - b.amount)
}

function buildFlatSingleCountryCandidates(
  smartCategories: SmartCategories | null,
  dualResult: DualEngineResult,
  userIso: string,
): RecommendationPlan[] {
  const coversUserIso = (plan: RecommendationPlan) =>
    (plan.data.support_countries || []).map((c) => String(c).toUpperCase()).includes(userIso)
  let candidates: RecommendationPlan[] = []
  if (smartCategories) {
    candidates = dedupeSmartPlans([
      ...smartCategories.single_country,
      ...smartCategories.multi_country,
      ...smartCategories.upgrades,
    ]).filter(coversUserIso)
  }
  if (candidates.length === 0) {
    const fromDual = [
      ...dualResult.exactMatches,
      ...dualResult.inventoryOffers,
    ].map((o) => o.plan)
    candidates = dedupeSmartPlans(fromDual).filter(coversUserIso)
  }
  return candidates
}

function computeFlatSingleCountryOrdered(
  candidates: RecommendationPlan[],
  userDay: number,
  userQuota: number,
  options: { userIso?: string; expanded?: boolean } = {}
): RecommendationPlan[] {
  const userIso = options.userIso || ""
  const expanded = options.expanded === true

  // 展开模式：保留同 fingerprint 的多个 provider 价格，给 power user 对比用。
  // 默认模式（与 Flutter 对齐）：按 (operators + features) fingerprint 去重，每组留最便宜的。
  const dedupeFinite = (plans: RecommendationPlan[]) =>
    expanded || !userIso
      ? tightDedupeFlatPlans(plans).sort((a, b) => a.amount - b.amount)
      : dedupeFlatByFingerprint(plans, userIso)

  const finite = dedupeFinite(
    candidates
      .filter((p) => p.data.day === userDay && !isUnlimitedSmartPlan(p))
      .filter((p) => userQuota !== -1 && (p.data.quota as number) === userQuota)
  )
  // unlimited 处理：
  // - 用户选了 unlimited（userQuota === -1）→ 显示所有不同能力的 unlimited（每能力组合最便宜的一张）
  // - 用户选了具体流量 + 默认模式 → 保留最便宜的 unlimited（"升级推荐"），同时保留任何带独特特性
  //   （phoneNumber/hotspot/topup/yohoCare）的 unlimited 变体，避免把用户在意的"特殊套餐"折叠掉。
  // - 展开模式：显示所有 unlimited 能力变体（供 power user 对比）
  const unlimitedAllVariants = dedupeFlatUnlimitedByCapability(
    candidates.filter((p) => p.data.day === userDay && isUnlimitedSmartPlan(p))
  )
  if (userQuota === -1) {
    return unlimitedAllVariants
  }
  const unlimited = expanded
    ? unlimitedAllVariants
    : keepCheapestPlusUniqueFeatures(unlimitedAllVariants)
  return [...finite, ...unlimited]
}

/**
 * Collapsed-mode 防御：从已按价格排序的 unlimited 集合中保留：
 *   - 最便宜的一张（"升级推荐"锚点）
 *   - 任何带 phoneNumber / hotspot / topup / yohoCare 的变体（用户在意的特殊能力）
 * 这样默认视图不会再把 16.90 含 phoneNumber 的 unlimited 折叠到 alternativeProviders 看不见。
 */
function keepCheapestPlusUniqueFeatures(
  plans: RecommendationPlan[]
): RecommendationPlan[] {
  if (plans.length <= 1) return plans
  const result: RecommendationPlan[] = [plans[0]]
  for (let i = 1; i < plans.length; i++) {
    const p = plans[i]
    const tg = p.tags
    const hasYohoCare =
      tg?.yohoCare === true || p.provider === "CMLink" || p.provider === "C"
    const isUnique = !!(
      tg?.phoneNumber ||
      tg?.hotspot ||
      tg?.topup ||
      hasYohoCare
    )
    if (isUnique) result.push(p)
  }
  return result
}

function pickSmartRecommendationSlots(
  smartCategories: SmartCategories | null,
  selectedIso2s: string[],
  userTripQuotaMB: number
): {
  cheapestSingle: RecommendationPlan | null
  phonePlan: RecommendationPlan | null
  cheapestMulti: RecommendationPlan | null
  cheapestUpgrade: RecommendationPlan | null
  /** Tier-bucketed offers (saver / fit / more / unlimited). */
  tierOffers: QuotaTierOffer[]
  /** @deprecated Kept for legacy callers; mirrors tierOffers with old slot field. */
  recommendedOffers: SmartRecommendationOffer[]
} {
  if (!smartCategories || selectedIso2s.length !== 1) {
    return {
      cheapestSingle: null,
      phonePlan: null,
      cheapestMulti: null,
      cheapestUpgrade: null,
      tierOffers: [],
      recommendedOffers: [],
    }
  }

  const userRequestedCountries = selectedIso2s.map((c) => c.toUpperCase())
  const coversAllUserCountries = (plan: RecommendationPlan) =>
    userRequestedCountries.every((iso2) => getSmartPlanCountries(plan).includes(iso2))

  // Build a single candidate pool from all categories (single_country, multi_country, upgrades)
  // that covers all user iso2s. The tier picker handles quota bucketing on its own.
  const candidates = dedupeSmartPlans([
    ...smartCategories.single_country,
    ...smartCategories.multi_country,
    ...smartCategories.upgrades,
  ]).filter(coversAllUserCountries)

  const tierOffers = pickQuotaTierOffers({ candidates, userTripQuotaMB })

  // Map tier → legacy slot for callers that haven't migrated yet.
  const legacyMap: Record<QuotaTier, SmartSlot> = {
    saver: "single",
    fit: "single",
    more: "multi",
    unlimited: "upgrade",
  }
  const recommendedOffers: SmartRecommendationOffer[] = tierOffers.map((t) => ({
    plan: t.plan,
    slot: legacyMap[t.tier],
  }))

  const tierByName = (name: QuotaTier) => tierOffers.find((t) => t.tier === name)?.plan || null

  return {
    cheapestSingle: tierByName("fit"),
    phonePlan: null,
    cheapestMulti: tierByName("more"),
    cheapestUpgrade: tierByName("unlimited"),
    tierOffers,
    recommendedOffers,
  }
}

function getSmartResultsExplanationKey(slots: {
  cheapestSingle: RecommendationPlan | null
  cheapestMulti: RecommendationPlan | null
  cheapestUpgrade: RecommendationPlan | null
}): string {
  if (slots.cheapestSingle && slots.cheapestMulti && slots.cheapestUpgrade) {
    return "results-explanation-all"
  }
  if (slots.cheapestSingle && slots.cheapestMulti) {
    return "results-explanation-single-multi"
  }
  if (slots.cheapestSingle && slots.cheapestUpgrade) {
    return "results-explanation-single-upgrade"
  }
  if (slots.cheapestSingle) {
    return "results-explanation-single"
  }
  return "results-explanation-fallback"
}

function getSmartResultsGridClass(recommendedOffers: SmartRecommendationOffer[]): string {
  return cn(
    "grid grid-cols-1 gap-5",
    recommendedOffers.length > 3
      ? "md:grid-cols-2 xl:grid-cols-4 xl:items-end"
      : "md:grid-cols-3 md:items-end"
  )
}

function getSmartOfferExplanation(
  offer: SmartRecommendationOffer,
  cheapestSingle: RecommendationPlan | null,
  t: (key: string, opts?: Record<string, unknown>) => string
): { explanation?: string; savings?: number } {
  if (offer.slot === "single") {
    return { explanation: t("recommendation-offer-explanation-single") }
  }

  if (offer.slot === "phone") {
    return { explanation: t("offer-phone-number") }
  }

  if (offer.slot === "upgrade") {
    return { explanation: t("recommendation-offer-explanation-unlimited") }
  }

  const multiIsCheaper = cheapestSingle ? offer.plan.amount < cheapestSingle.amount : false
  const savings = multiIsCheaper && cheapestSingle
    ? Math.round((cheapestSingle.amount - offer.plan.amount) / cheapestSingle.amount * 100)
    : undefined

  return {
    savings,
    explanation: multiIsCheaper && cheapestSingle
      ? t("recommendation-offer-explanation-multi", {
          count: offer.plan.data.support_countries?.length || 0,
          savings: ((cheapestSingle.amount - offer.plan.amount) / 100).toFixed(2),
        })
      : t("recommendation-offer-explanation-multi-coverage", {
          count: offer.plan.data.support_countries?.length || 0,
        }),
  }
}

function getRenderableOfferCount(
  dualResult: DualEngineResult,
  smartCategories: SmartCategories | null,
  selectedIso2s: string[],
  userTripQuotaMB: number,
  userDay: number,
): number {
  // Single-country: use the same flat-ordered algorithm the grid renders with,
  // so the header count and the rendered card count always match.
  if (selectedIso2s.length === 1) {
    const userIso = selectedIso2s[0].toUpperCase()
    const candidates = buildFlatSingleCountryCandidates(smartCategories, dualResult, userIso)
    const ordered = computeFlatSingleCountryOrdered(candidates, userDay, userTripQuotaMB, { userIso })
    if (ordered.length > 0) return ordered.length
    if (dualResult.exactMatches.length === 0 && dualResult.nearMatches.length > 0) {
      return dualResult.nearMatches.length
    }
    return 0
  }

  const userRequestedCountries = selectedIso2s.map((c) => c.toUpperCase())

  const allOffers = [...dualResult.exactMatches, ...dualResult.inventoryOffers]
  const singleCountryOffers = allOffers.filter((offer) => {
    if (offer.isUpgradeNudge) return false
    const countries = (offer.plan.data?.support_countries || []).map((c) => String(c).toUpperCase())
    return userRequestedCountries.length === 1
      && countries.length === 1
      && countries[0] === userRequestedCountries[0]
  })
  const cheapestSingleCountry = singleCountryOffers.length > 0
    ? [...singleCountryOffers].sort((a, b) => Number(a.plan.amount) - Number(b.plan.amount))[0]
    : null

  const multiCountryOffers = allOffers.filter((offer) => {
    if (offer.isUpgradeNudge) return false
    const countries = (offer.plan.data?.support_countries || []).map((c) => String(c).toUpperCase())
    return countries.length > 1 && userRequestedCountries.every((c) => countries.includes(c))
  })
  const cheapestMultiCountry = multiCountryOffers.length > 0
    ? [...multiCountryOffers].sort((a, b) => Number(a.plan.amount) - Number(b.plan.amount))[0]
    : null

  const upgradeOffers = allOffers.filter((offer) => offer.isUpgradeNudge)
  const cheapestUpgrade = upgradeOffers.length > 0 ? upgradeOffers[0] : null
  const recommendedCount = [cheapestSingleCountry, cheapestMultiCountry, cheapestUpgrade].filter(Boolean).length

  if (recommendedCount > 0) return recommendedCount
  if (dualResult.exactMatches.length === 0 && dualResult.nearMatches.length > 0) return dualResult.nearMatches.length
  return 0
}

const DEFAULT_TOTAL_OPTIONS = [
  { label: "1GB", value: 1024 },
  { label: "3GB", value: 3072 },
  { label: "5GB", value: 5120 },
  { label: "10GB", value: 10240 },
  { label: "20GB", value: 20480 },
  { label: "50GB", value: 51200 },
  { label: "Unlimited", value: -1 },
]

const DAILY_DATA_OPTIONS = [
  { label: "500MB", labelEN: "500MB", value: 500 },
  { label: "1GB", labelEN: "1GB", value: 1024 },
  { label: "2GB", labelEN: "2GB", value: 2048 },
  { label: "3GB", labelEN: "3GB", value: 3072 },
  { label: "5GB", labelEN: "5GB", value: 5120 },
  { label: "Unlimited", labelEN: "Unlimited", value: -1 },
]

// =============================================================================
// Country Picker Modal
// =============================================================================

function CountryPickerModal({
  show,
  onClose,
  countries,
  selectedIso2s,
  onToggle,
  t,
}: {
  show: boolean
  onClose: () => void
  countries: CountryEntry[]
  selectedIso2s: Set<string>
  onToggle: (iso2: string) => void
  t: (key: string, options?: { count?: number }) => string
}) {
  const [query, setQuery] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden"
      setTimeout(() => inputRef.current?.focus(), 100)
    } else {
      document.body.style.overflow = ""
      setQuery("")
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [show])

  useEffect(() => {
    if (!show) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [show, onClose])

  // Sort: selected countries first, then alphabetical
  const sorted = useMemo(() => {
    return [...countries].sort((a, b) => {
      const aSelected = selectedIso2s.has(a.iso2) ? 0 : 1
      const bSelected = selectedIso2s.has(b.iso2) ? 0 : 1
      if (aSelected !== bSelected) return aSelected - bSelected
      return a.name.localeCompare(b.name)
    })
  }, [countries, selectedIso2s])

  // Cross-language search: match translated name, English name, or ISO2 code
  const filtered = useMemo(() => {
    if (!query) return sorted
    const q = query.toLowerCase()
    return sorted.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.nameEN.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q)
    )
  }, [sorted, query])

  if (!show) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[85vh] w-full flex-col rounded-t-2xl bg-white md:max-h-[80vh] md:max-w-[560px] md:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold">
              {t("country-picker-title")}
            </h3>
            <p className="mt-0.5 text-xs text-gray-400">
              {t("country-picker-selected-count", { count: selectedIso2s.size })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="border-b px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("country-picker-search-placeholder")}
              className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="m15 9-6 6" />
                  <path d="m9 9 6 6" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-2">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              {t("country-picker-no-results")}
            </div>
          ) : (
            filtered.map((c) => {
              const selected = selectedIso2s.has(c.iso2)
              return (
                <button
                  key={c.iso2}
                  onClick={() => onToggle(c.iso2)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-gray-50",
                    selected && "bg-orange-50"
                  )}
                >
                  <Image src={getFlagUrl(c.iso2)} alt="" width={20} height={20} className="inline-block rounded-[2px] object-cover" />
                  <span className={cn("flex-1 text-sm", selected && "font-semibold text-home-orange_1")}>
                    {c.name}
                  </span>
                  {selected && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF7A00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              )
            })
          )}
        </div>

        {/* Done button */}
        <div className="border-t px-5 py-4">
          <button
            onClick={onClose}
            className="w-full rounded-full bg-home-orange_1 py-3 text-sm font-semibold text-white transition-colors hover:bg-home-orange_1/90"
          >
            {t("country-picker-done")}
          </button>
        </div>
      </div>
    </div>
  )
}

// Locale-aware list separator: CJK languages use "、", others use ", "
function listSep(lng?: string) {
  if (!lng) return ", "
  const cjk = ["zh", "ja"]
  return cjk.some((c) => lng.startsWith(c)) ? "、" : ", "
}

// =============================================================================
// Main Component
// =============================================================================

export default function TripConfigurator({
  baseIso2s,
  baseCountries,
  countryName,
  lng,
  initialPlans,
  initialCacheKey,
  availableCountries,
  initialTotalOptions,
  initialDailyOptions,
  countryPresets,
}: TripConfiguratorProps) {
  const { t, ready } = useTranslation(lng, "product")
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // --- Initialize state from URL params or defaults ---
  const [selectedIso2s, setSelectedIso2s] = useState<Set<string>>(() => {
    const urlCountries = searchParams.get("countries")
    if (urlCountries) {
      const codes = urlCountries.split(",").filter(Boolean)
      if (codes.length > 0) return new Set(codes)
    }
    return new Set(baseIso2s)
  })
  const [days, setDays] = useState(() => {
    const urlDay = searchParams.get("day")
    if (urlDay) {
      const d = parseInt(urlDay, 10)
      if (!isNaN(d) && d >= MIN_DAYS && d <= MAX_DAYS) return d
    }
    return 7
  })
  const [daysInput, setDaysInput] = useState(String(days))
  const daysInputFocused = useRef(false)
  const [dataMode, setDataMode] = useState<DataMode>(() => {
    const urlMode = searchParams.get("dataMode")
    // Map "unlimited" to "total" mode — unlimited is now a tick on the total slider
    if (urlMode === "unlimited") return "total"
    if (urlMode === "total" || urlMode === "daily") return urlMode
    return "total"
  })
  // totalQuotaMB: stores the quota in MB (-1 = unlimited)
  const [totalQuotaMB, setTotalQuotaMB] = useState(() => {
    const urlQuota = searchParams.get("quota")
    if (urlQuota) {
      if (urlQuota === "unlimited" || urlQuota === "-1") return -1
      const q = parseInt(urlQuota, 10)
      if (!isNaN(q) && q > 0) return q
    }
    return 10240 // default: 10GB
  })
  const [dailyDataIdx, setDailyDataIdx] = useState(() => {
    const urlQuota = searchParams.get("quota")
    if (urlQuota) {
      const q = parseInt(urlQuota, 10)
      const idx = DAILY_DATA_OPTIONS.findIndex((o) => o.value === q)
      if (idx >= 0) return idx
    }
    return 1 // default: 1GB
  })
  const [allFetchedPlans, setAllFetchedPlans] = useState<RecommendationPlan[]>(initialPlans)
  // "查看全部 X 个套餐" 折叠：默认按 fingerprint 去重显示 4-6 张（Flutter 对齐），
  // 展开后显示所有 provider 的同能力套餐供 power user 对比。
  const [showAllFlatPlans, setShowAllFlatPlans] = useState(false)
  // 🆕 Store smart API categorized results (bypass resolveDualEngine filtering)
  const [smartCategories, setSmartCategories] = useState<SmartCategories | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCountryPicker, setShowCountryPicker] = useState(false)
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null)
  const [persistedResults, setPersistedResults] = useState<ResultsSnapshot | null>(null)
  const [showResultsRefreshing, setShowResultsRefreshing] = useState(false)
  const [showResultsUpdated, setShowResultsUpdated] = useState(false)

  const trackConfiguratorViewRef = useRef(false)

  const getTrackingCountryCode = useCallback(() => Array.from(selectedIso2s).sort().join(","), [selectedIso2s])

  const trackTripDurationChange = useCallback((nextDays: number, source: string) => {
    trackYohoEvent("trip_duration_change", {
      previous_days: days,
      next_days: nextDays,
      country_code: getTrackingCountryCode(),
      source,
    })
  }, [days, getTrackingCountryCode])

  const trackTripQuotaChange = useCallback((previousQuota: number, nextQuota: number, source: string) => {
    trackYohoEvent("trip_quota_change", {
      previous_quota: previousQuota,
      next_quota: nextQuota,
      country_code: getTrackingCountryCode(),
      source,
    })
  }, [getTrackingCountryCode])

  const selectPlanCard = useCallback((planKeyValue: string, plan: RecommendationPlan, source: string) => {
    trackYohoEvent("plan_card_select", {
      plan_id: plan.data.variant_id || plan.data.product_id,
      country_code: getTrackingCountryCode(),
      price: plan.amount,
      source,
    })
    setSelectedPlanKey(planKeyValue)
  }, [getTrackingCountryCode])

  useEffect(() => {
    if (trackConfiguratorViewRef.current) return
    trackConfiguratorViewRef.current = true
    trackYohoEvent("product_configurator_view", {
      country_code: getTrackingCountryCode(),
      plan_count: initialPlans.length,
    })
  }, [getTrackingCountryCode, initialPlans.length])

  // --- Dynamic quota options (probe-driven) ---
  const [dynamicQuotas, setDynamicQuotas] = useState<Array<{ label: string; value: number }>>(
    () => initialTotalOptions && initialTotalOptions.length >= 3 ? initialTotalOptions : DEFAULT_TOTAL_OPTIONS
  )
  const [dynamicDailyQuotas, setDynamicDailyQuotas] = useState<
    Array<{ label: string; labelEN: string; value: number }>
  >(() => initialDailyOptions && initialDailyOptions.length >= 2 ? initialDailyOptions : DAILY_DATA_OPTIONS)
  const [probing, setProbing] = useState(false)

  const cache = useRef<Map<string, RecommendationPlan[]>>(new Map())
  const probeCache = useRef<
    Map<string, {
      totalOptions: Array<{ label: string; value: number }>
      dailyOptions: Array<{ label: string; labelEN: string; value: number }>
      allProbedPlans: RecommendationPlan[]
    }>
  >(new Map())
  const probeAbortRef = useRef<AbortController | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)
  // Track whether probe has completed at least once (to prevent premature smart-reset)
  const hasCompletedProbeRef = useRef(false)
  // Track whether user has manually changed quota (to avoid overriding with auto-recommendation)
  const quotaManualRef = useRef(!!searchParams.get("quota"))
  const preserveInitialQuotaRef = useRef(searchParams.has("quota"))
  const lastResolvedRequestRef = useRef<string | null>(null)
  const refreshStartedAtRef = useRef<number | null>(null)

  // User manually sets quota — marks as manual to prevent auto-override
  const setQuotaManual = useCallback((mb: number, source = "manual") => {
    preserveInitialQuotaRef.current = false
    quotaManualRef.current = true
    trackTripQuotaChange(totalQuotaMB, mb, source)
    setTotalQuotaMB(mb)
  }, [totalQuotaMB, trackTripQuotaChange])

  // Quota presets for quick-select chips (GB values in MB, -1 = unlimited)
  const QUOTA_PRESETS = [1024, 3072, 5120, 10240, 20480, 51200, -1]
  const MIN_QUOTA_GB = 1
  const MAX_QUOTA_GB = 50
  // Convert totalQuotaMB to GB for slider/stepper (clamp for non-unlimited)
  const totalQuotaGB = totalQuotaMB === -1 ? MAX_QUOTA_GB : Math.round(totalQuotaMB / 1024)
  const isUnlimited = totalQuotaMB === -1
  const visibleTotalQuotaOptions = useMemo(() => {
    const options = [...dynamicQuotas]
    if (totalQuotaMB > 0 && !options.some((opt) => opt.value === totalQuotaMB)) {
      options.push({ label: formatQuota(totalQuotaMB), value: totalQuotaMB })
      options.sort((a, b) => a.value - b.value)
    }
    return options
  }, [dynamicQuotas, totalQuotaMB])

  // Auto-recommend quota when days change (only if user hasn't manually set quota)
  // Base: ~1.5 GB/day for moderate travel use, snapped to nearest available option
  const dataModeRef = useRef(dataMode)
  dataModeRef.current = dataMode
  const totalQuotaRef = useRef(totalQuotaMB)
  totalQuotaRef.current = totalQuotaMB
  const dynamicQuotasRef = useRef(dynamicQuotas)
  dynamicQuotasRef.current = dynamicQuotas
  useEffect(() => {
    if (quotaManualRef.current) return // user already chose — don't override
    if (dataModeRef.current !== "total") return // only applies to total mode
    if (totalQuotaRef.current === -1) return // don't downgrade from unlimited

    // For short trips: ~1.5 GB/day. For long-term (>30d): first 30 days at 1.5GB, rest at 0.5GB
    const shortDays = Math.min(days, 30)
    const longDays = Math.max(0, days - 30)
    const ideal = shortDays * 1536 + longDays * 512
    // Snap to the nearest available option (dynamic or fallback QUOTA_PRESETS)
    const available = dynamicQuotasRef.current.filter((o) => o.value > 0).map((o) => o.value)
    const presets = available.length >= 3 ? available : QUOTA_PRESETS.filter((q) => q > 0)
    let best = presets[0]
    let bestDist = Math.abs(ideal - best)
    for (const p of presets) {
      const dist = Math.abs(ideal - p)
      if (dist < bestDist) { best = p; bestDist = dist }
    }
    if (best !== totalQuotaRef.current) {
      setTotalQuotaMB(best)
    }
  }, [days]) // eslint-disable-line react-hooks/exhaustive-deps

  // Smart-reset totalQuotaMB when dynamic total options change
  // If the current selection isn't among the new options, snap to the nearest available value
  // Skip until probe has completed at least once — SSR initial options may be incomplete
  useEffect(() => {
    if (!hasCompletedProbeRef.current) return // wait for probe to finish
    if (totalQuotaMB === -1) return // unlimited is always valid
    if (preserveInitialQuotaRef.current) return
    const availableValues = dynamicQuotas.filter((o) => o.value > 0).map((o) => o.value)
    if (availableValues.length === 0) return
    if (availableValues.includes(totalQuotaMB)) return // current selection is valid
    // Snap to nearest available value
    let best = availableValues[0]
    let bestDist = Math.abs(totalQuotaMB - best)
    for (const v of availableValues) {
      const dist = Math.abs(totalQuotaMB - v)
      if (dist < bestDist) { best = v; bestDist = dist }
    }
    setTotalQuotaMB(best)
  }, [dynamicQuotas]) // eslint-disable-line react-hooks/exhaustive-deps

  // Smart-reset dailyDataIdx when options change
  useEffect(() => {
    const prevValue = DAILY_DATA_OPTIONS[dailyDataIdx]?.value
      ?? dynamicDailyQuotas[dailyDataIdx]?.value
    if (prevValue) {
      const newIdx = dynamicDailyQuotas.findIndex((o) => o.value === prevValue)
      if (newIdx >= 0) { setDailyDataIdx(newIdx); return }
    }
    setDailyDataIdx(Math.min(1, dynamicDailyQuotas.length - 1))
  }, [dynamicDailyQuotas]) // eslint-disable-line react-hooks/exhaustive-deps

  // Derive API quota+daily from mode
  const apiParams = useMemo(() => {
    switch (dataMode) {
      case "total":
        return { quota: totalQuotaMB, daily: false }
      case "daily":
        return {
          quota: dynamicDailyQuotas[Math.min(dailyDataIdx, dynamicDailyQuotas.length - 1)]?.value ?? 1024,
          daily: true,
        }
      case "unlimited":
        return { quota: -1, daily: false }
    }
  }, [dataMode, totalQuotaMB, dailyDataIdx, dynamicDailyQuotas])

  // Sync state to URL params (shallow, no navigation)
  useEffect(() => {
    const params = new URLSearchParams()
    params.set("countries", Array.from(selectedIso2s).sort().join(","))
    params.set("day", String(days))
    params.set("dataMode", dataMode)
    params.set("quota", String(apiParams.quota))
    const newUrl = `${pathname}?${params.toString()}`
    window.history.replaceState(null, "", newUrl)
  }, [selectedIso2s, days, dataMode, apiParams.quota, pathname])

  // Cache initial plans
  useEffect(() => {
    const key = initialCacheKey || `${buildCountriesParam(baseIso2s)}_7_10240`
    cache.current.set(key, initialPlans)
  }, [baseIso2s, initialCacheKey, initialPlans]) // eslint-disable-line react-hooks/exhaustive-deps

  // Countries param for API (uses underscore separator)
  const countriesParam = useMemo(
    () => buildCountriesParam(Array.from(selectedIso2s)),
    [selectedIso2s]
  )

  // Countries param for buy URL (uses comma for package page)
  const countriesForBuyUrl = useMemo(
    () => Array.from(selectedIso2s).sort().join(","),
    [selectedIso2s]
  )

  useEffect(() => {
    if (selectedIso2s.size !== 1) {
      setSmartCategories(null)
    }
  }, [selectedIso2s.size])

  // Country toggle handler
  const toggleCountry = useCallback((iso2: string) => {
    preserveInitialQuotaRef.current = false
    setSelectedIso2s((prev) => {
      const next = new Set(prev)
      if (next.has(iso2)) {
        if (next.size > 1) next.delete(iso2)
      } else {
        next.add(iso2)
      }
      return next
    })
  }, [])

  // Quick preset handlers
  const applyPreset = useCallback(
    (iso2s: string[]) => {
      preserveInitialQuotaRef.current = false
      setSelectedIso2s(new Set(iso2s))
    },
    []
  )

  // Day handlers — changing days resets manual quota lock so auto-recommend kicks in
  const adjustDays = useCallback((delta: number) => {
    preserveInitialQuotaRef.current = false
    quotaManualRef.current = false
    const nextDays = Math.max(MIN_DAYS, Math.min(MAX_DAYS, days + delta))
    trackTripDurationChange(nextDays, "stepper")
    setDays(nextDays)
  }, [days, trackTripDurationChange])

  const setDaysClamped = useCallback((v: number, source = "input") => {
    preserveInitialQuotaRef.current = false
    quotaManualRef.current = false
    const nextDays = Math.max(MIN_DAYS, Math.min(MAX_DAYS, v))
    trackTripDurationChange(nextDays, source)
    setDays(nextDays)
  }, [trackTripDurationChange])

  // ===== Phase 1: Probe available quota options (triggered by country/days change) =====
  const probeAvailableQuotas = useCallback(async () => {
    if (selectedIso2s.size === 0) return

    const cacheKey = `${countriesParam}_${days}`
    const cached = probeCache.current.get(cacheKey)
    if (cached) {
      setDynamicQuotas(cached.totalOptions)
      setDynamicDailyQuotas(cached.dailyOptions)
      return
    }

    probeAbortRef.current?.abort()
    const controller = new AbortController()
    probeAbortRef.current = controller

    setProbing(true)
    try {
      const probeParams = getProbeSearchParams(days)
      const results = await Promise.all(
        probeParams.map(async (p) => {
          const quotaStr = typeof p.quota === "string" ? p.quota : String(p.quota)
          const url = `${API_BASE}?countries=${countriesParam}&quota=${quotaStr}&day=${p.day}&version=7`
          try {
            const res = await fetch(url, { signal: controller.signal })
            const json = await res.json()
            if (json.is_success && json.data?.recommendation_list) {
              return normalizePlans(json.data.recommendation_list as RecommendationPlan[])
            }
            return []
          } catch {
            return []
          }
        })
      )

      if (controller.signal.aborted) return

      const allProbedPlans = results.flat()

      // Extract total-mode quota options
      const totalQuotaSet = new Set<number>()
      for (const plan of allProbedPlans) {
        if (plan.data.quota > 0 && !plan.data.daily) {
          totalQuotaSet.add(plan.data.quota)
        }
      }
      const totalOptions = totalQuotaSet.size >= 3
        ? getDynamicQuotaOptions(Array.from(totalQuotaSet).sort((a, b) => a - b))
        : DEFAULT_TOTAL_OPTIONS

      // Extract daily-mode quota options
      const dailyResult = getDynamicDailyOptions(allProbedPlans)
      const dailyOptions = dailyResult || DAILY_DATA_OPTIONS

      // Store probe results in cache
      probeCache.current.set(cacheKey, { totalOptions, dailyOptions, allProbedPlans })

      // Also seed the plan cache so dual-engine search can reuse probed plans
      for (const p of probeParams) {
        const planCacheKey = `${countriesParam}_${days}_${p.quota}`
        if (!cache.current.has(planCacheKey)) {
          cache.current.set(
            planCacheKey,
            allProbedPlans.filter((plan) => plan.data.quota === p.quota && !plan.data.daily)
          )
        }
      }

      setDynamicQuotas(totalOptions)
      setDynamicDailyQuotas(dailyOptions)
      hasCompletedProbeRef.current = true
    } catch {
      // Keep current options on failure
    } finally {
      if (!controller.signal.aborted) setProbing(false)
    }
  }, [countriesParam, days, selectedIso2s.size])

  useEffect(() => {
    probeAvailableQuotas()
  }, [probeAvailableQuotas])

  // ===== Phase 2: Dual Engine Search (triggered by quota/mode change) =====
  const fetchDualEngine = useCallback(async () => {
    if (selectedIso2s.size === 0) return

    searchAbortRef.current?.abort()
    const controller = new AbortController()
    searchAbortRef.current = controller

    setLoading(true)
    try {
      // Snap quota to nearest backend-supported value before API call
      const snappedQuota = snapQuotaForAPI(apiParams.quota)
      const quotaParam = String(snappedQuota)

      // 🆕 For single-country queries, use smart recommendations API
      const isSingleCountry = selectedIso2s.size === 1
      if (!isSingleCountry) {
        setSmartCategories(null)
      }

      if (isSingleCountry) {
        const smartUrl = `${SMART_API_BASE}?countries=${countriesParam}&quota=${quotaParam}&day=${days}&version=7`

        try {
          const res = await fetch(smartUrl, { signal: controller.signal })
          const json = await res.json()

          if (controller.signal.aborted) return

          if (json.is_success && json.data) {
            // 保存智能 API 的原始分类数据（绕过 resolveDualEngine 过滤）
            const categorized = {
              single_country: normalizePlans(json.data.single_country || []),
              multi_country: normalizePlans(json.data.multi_country || []),
              upgrades: normalizePlans(json.data.upgrades || [])
            }


            setSmartCategories(categorized)

            // 仍然设置 allFetchedPlans 用于其他逻辑
            const allPlans = [
              ...categorized.single_country,
              ...categorized.multi_country,
              ...categorized.upgrades
            ]
            setAllFetchedPlans(allPlans)
            return
          }
          setSmartCategories(null)
        } catch (err) {
          // 如果智能 API 失败，降级到旧的双引擎搜索
          setSmartCategories(null)
          console.warn('Smart API failed, falling back to dual engine', err)
        }
      }

      // Engine A: exact user request
      const exactUrl = `${API_BASE}?countries=${countriesParam}&quota=${quotaParam}&day=${days}&version=7`

      // Engine B: inventory search — wider parameter combos
      const inventoryParams = getInventorySearchParams(apiParams.quota, days, apiParams.daily)
      const inventoryUrls = inventoryParams.map(
        (p) => {
          const iq = typeof p.quota === "string" ? p.quota : String(snapQuotaForAPI(p.quota as number))
          return `${API_BASE}?countries=${countriesParam}&quota=${iq}&day=${p.day}&version=7`
        }
      )

      // Check cache for exact request
      const exactCacheKey = `${countriesParam}_${days}_${apiParams.quota}`
      const cachedExact = cache.current.get(exactCacheKey)

      // Merge probed plans from Phase 1
      const probeCacheKey = `${countriesParam}_${days}`
      const probedPlans = probeCache.current.get(probeCacheKey)?.allProbedPlans || []

      // Parallel fetch: exact + all inventory
      const allUrls = cachedExact ? inventoryUrls : [exactUrl, ...inventoryUrls]
      const results = await Promise.all(
        allUrls.map(async (url) => {
          try {
            const res = await fetch(url, { signal: controller.signal })
            const json = await res.json()
            if (json.is_success && json.data?.recommendation_list) {
              return normalizePlans(json.data.recommendation_list as RecommendationPlan[])
            }
            return []
          } catch {
            return []
          }
        })
      )

      if (controller.signal.aborted) return

      // Merge all plans (fresh + cached exact + probed)
      let allPlans: RecommendationPlan[]
      if (cachedExact) {
        allPlans = [...cachedExact, ...results.flat(), ...probedPlans]
      } else {
        const exactPlans = results[0] || []
        cache.current.set(exactCacheKey, exactPlans)
        allPlans = [...results.flat(), ...probedPlans]
      }

      setAllFetchedPlans(allPlans)
    } catch {
      if (!controller.signal.aborted) setAllFetchedPlans([])
    } finally {
      // Always clear loading for the active controller
      if (searchAbortRef.current === controller) {
        setLoading(false)
      }
    }
  }, [countriesParam, days, apiParams.quota, apiParams.daily, selectedIso2s.size])

  useEffect(() => {
    fetchDualEngine()
  }, [fetchDualEngine])

  // --- Dual Engine Resolve ---
  const dualResult: DualEngineResult = useMemo(
    () => {
      return resolveDualEngine(
        allFetchedPlans,
        Array.from(selectedIso2s),
        days,
        apiParams.quota,
        apiParams.daily,
        t
      )
    },
    [allFetchedPlans, selectedIso2s, days, apiParams.quota, apiParams.daily, t]
  )

  // Plan key for selection tracking
  const planKey = useCallback((p: RecommendationPlan) =>
    `${p.provider}_${p.data.quota}_${p.data.day}_${p.data.daily}_${p.amount}`, [])

  useEffect(() => {
    if (dualResult.exactMatches.length > 0) {
      // 升级推荐（isUpgradeNudge，通常是 unlimited 套餐）优先；如果有多张则挑「最便宜」的，
      // 而不是数组里第一张（之前 .find() 拿到的是第一个匹配，不一定最便宜）。
      const upgradeNudges = dualResult.exactMatches.filter((o) => o.isUpgradeNudge)
      const cheapestUpgrade =
        upgradeNudges.length > 0
          ? upgradeNudges.reduce((cheapest, o) =>
              o.plan.amount < cheapest.plan.amount ? o : cheapest
            )
          : null
      const featuredExact = cheapestUpgrade ?? dualResult.exactMatches[0]
      setSelectedPlanKey(planKey(featuredExact.plan))
    } else if (dualResult.nearMatches.length > 0) {
      setSelectedPlanKey(planKey(dualResult.nearMatches[0].plan))
    } else {
      setSelectedPlanKey(null)
    }
  }, [dualResult, planKey])

  const totalOffers =
    dualResult.exactMatches.length +
    dualResult.nearMatches.length +
    dualResult.inventoryOffers.length

  // Trip-total user quota in MB (daily quota * days, total quota, or Infinity for unlimited).
  // Used by the quota-tier slot picker to pick saver / fit / more / unlimited cards.
  const userTripQuotaMB = useMemo(() => {
    if (dataMode === "daily") {
      const opt = dynamicDailyQuotas[dailyDataIdx]
      if (!opt || opt.value === -1) return Number.POSITIVE_INFINITY
      return opt.value * Math.max(1, days)
    }
    if (totalQuotaMB === -1) return Number.POSITIVE_INFINITY
    return totalQuotaMB
  }, [dataMode, dynamicDailyQuotas, dailyDataIdx, totalQuotaMB, days])

  const visibleOfferCount = useMemo(
    () => getRenderableOfferCount(dualResult, smartCategories, Array.from(selectedIso2s), userTripQuotaMB, days),
    [dualResult, smartCategories, selectedIso2s, userTripQuotaMB, days]
  )

  const primaryOfferCount = dualResult.exactMatches.length + dualResult.nearMatches.length
  const hasMultipleResults = primaryOfferCount >= 2
  const hasSingleResult = primaryOfferCount === 1

  useEffect(() => {
    if (loading || visibleOfferCount <= 0) return
    setPersistedResults({
      totalOffers,
      dualResult,
      smartCategories,
      iso2s: Array.from(selectedIso2s),
      countriesForBuyUrl,
      selectedPlanKey,
    })
  }, [loading, visibleOfferCount, totalOffers, dualResult, smartCategories, selectedIso2s, countriesForBuyUrl, selectedPlanKey])

  const displayedResults = loading && persistedResults
    ? persistedResults
    : {
        totalOffers,
        dualResult,
        smartCategories,
        iso2s: Array.from(selectedIso2s),
        countriesForBuyUrl,
        selectedPlanKey,
      }
  const displayedDualResult = displayedResults.dualResult
  const displayedSmartCategories = displayedResults.smartCategories
  const displayedIso2s = displayedResults.iso2s
  const displayedCountriesForBuyUrl = displayedResults.countriesForBuyUrl
  const displayedSelectedPlanKey = displayedResults.selectedPlanKey
  const displayedVisibleOfferCount = useMemo(
    () => getRenderableOfferCount(displayedDualResult, displayedSmartCategories, displayedIso2s, userTripQuotaMB, days),
    [displayedDualResult, displayedSmartCategories, displayedIso2s, userTripQuotaMB, days]
  )
  const hasMultipleVisibleResults = displayedVisibleOfferCount >= 2
  const hasSingleVisibleResult = displayedVisibleOfferCount === 1

  useEffect(() => {
    if (loading && displayedVisibleOfferCount > 0) {
      refreshStartedAtRef.current = Date.now()
      setShowResultsRefreshing(true)
      return
    }

    const startedAt = refreshStartedAtRef.current
    if (!startedAt) {
      setShowResultsRefreshing(false)
      return
    }

    const elapsed = Date.now() - startedAt
    const remaining = Math.max(0, 400 - elapsed)
    const timer = window.setTimeout(() => {
      refreshStartedAtRef.current = null
      setShowResultsRefreshing(false)
    }, remaining)
    return () => window.clearTimeout(timer)
  }, [displayedVisibleOfferCount, loading])

  const lowestRaw = useMemo(() => {
    const allOfferAmounts = [
      ...dualResult.exactMatches.map((o) => o.plan.amount),
      ...dualResult.nearMatches.map((o) => o.plan.amount),
      ...dualResult.inventoryOffers.map((o) => o.plan.amount),
    ].filter((a) => a > 0)
    return allOfferAmounts.length > 0 ? Math.min(...allOfferAmounts) : 0
  }, [dualResult])

  const lowestFormatted = lowestRaw > 0 ? formatPrice(lowestRaw) : null

  // --- GTM Analytics Events ---
  useEffect(() => {
    if (loading) return
    const w = window as typeof window & { dataLayer?: Record<string, unknown>[] }
    const baseParams = {
      country: Array.from(selectedIso2s).join(","),
      days,
      dataMode: apiParams.daily ? "daily" : apiParams.quota === -1 ? "unlimited" : "total",
      quota: apiParams.quota,
      recommendationCount: totalOffers,
      primaryOfferCount,
      hasMultipleResults,
      hasSingleResult,
    }

    if (hasMultipleResults) {
      w.dataLayer?.push({ event: "multi_result_state_view", ...baseParams })
    } else if (hasSingleResult) {
      w.dataLayer?.push({ event: "single_result_state_view", ...baseParams })
    } else if (dualResult.nearMatches.length > 0) {
      w.dataLayer?.push({ event: "no_exact_match_view", ...baseParams, nearMatchCount: dualResult.nearMatches.length })
    } else if (totalOffers === 0) {
      w.dataLayer?.push({ event: "zero_result_state_view", ...baseParams })
    }

    if (totalOffers > 0) {
      w.dataLayer?.push({ event: "recommendation_view", ...baseParams })
    }
  }, [loading, totalOffers, dualResult, selectedIso2s, days, apiParams, hasMultipleResults, hasSingleResult, primaryOfferCount])

  // Country name lookup
  const countryNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of availableCountries) {
      map.set(c.iso2, c.name)
    }
    return map
  }, [availableCountries])

  const selectedCountryNames = useMemo(
    () =>
      Array.from(selectedIso2s).map(
        (iso2) => countryNameMap.get(iso2) || iso2
      ),
    [selectedIso2s, countryNameMap]
  )

  // Summary data label
  const summaryDataLabel = useMemo(() => {
    switch (dataMode) {
      case "total": {
        if (totalQuotaMB === -1) return t("configurator-data-unlimited")
        return formatQuota(totalQuotaMB)
      }
      case "daily": {
        const opt = dynamicDailyQuotas[Math.min(dailyDataIdx, dynamicDailyQuotas.length - 1)]
        const dataDisplay = opt?.value === -1 ? t("configurator-data-unlimited") : (opt?.labelEN ?? "1 GB")
        return t("summary-daily-label", { data: dataDisplay })
      }
      case "unlimited":
        return t("configurator-data-unlimited")
    }
  }, [dataMode, totalQuotaMB, dailyDataIdx, dynamicDailyQuotas, t])

  // Summary mode label
  const summaryModeLabel = useMemo(() => {
    switch (dataMode) {
      case "total":
        return totalQuotaMB === -1 ? t("data-mode-unlimited") : t("data-mode-total")
      case "daily": return t("data-mode-daily")
      case "unlimited": return t("data-mode-unlimited")
    }
  }, [dataMode, totalQuotaMB, t])
  const requestSummaryLabel = useMemo(
    () => [
      selectedCountryNames.join(listSep(lng)),
      days === 1 ? t("step-days-unit-singular", { count: days }) : t("step-days-unit", { count: days }),
      summaryDataLabel,
    ].join(" · "),
    [selectedCountryNames, days, lng, summaryDataLabel, t]
  )
  const activeRequestKey = useMemo(
    () => `${countriesParam}|${days}|${dataMode}|${apiParams.quota}`,
    [countriesParam, days, dataMode, apiParams.quota]
  )

  useEffect(() => {
    if (loading) {
      setShowResultsUpdated(false)
      return
    }
    if (visibleOfferCount <= 0) return

    if (lastResolvedRequestRef.current === null) {
      lastResolvedRequestRef.current = activeRequestKey
      return
    }
    if (lastResolvedRequestRef.current === activeRequestKey) return

    lastResolvedRequestRef.current = activeRequestKey
    setShowResultsUpdated(true)
    const timer = window.setTimeout(() => setShowResultsUpdated(false), 1800)
    return () => window.clearTimeout(timer)
  }, [activeRequestKey, loading, trackTripDurationChange, visibleOfferCount])

  // Long-term mode: days > 30 hides slider, shows only input
  const isLongTerm = days > SLIDER_MAX

  // Keep daysInput in sync when days changes externally (presets, ± buttons, slider)
  // Skip sync while user is actively typing in the input
  useEffect(() => { if (!daysInputFocused.current) setDaysInput(String(days)) }, [days])

  // Slider percentage for fill (only used when days <= 30)
  const sliderPercent = ((Math.min(days, SLIDER_MAX) - MIN_DAYS) / (SLIDER_MAX - MIN_DAYS)) * 100

  if (!ready) {
    return (
      <section id="configurator" className="border-y border-gray-100 bg-gray-50/30">
        <div className="mx-auto max-w-[1200px] px-5 py-14 md:px-10 md:py-20">
          <div className="mx-auto mb-10 h-4 w-28 rounded-full bg-gray-200" />
          <div className="mx-auto mb-4 h-10 max-w-md rounded-full bg-gray-200" />
          <div className="mx-auto mb-10 h-4 max-w-2xl rounded-full bg-gray-100" />
          <div className="flex flex-col gap-8 md:flex-row">
            <div className="space-y-4 md:flex-1">
              <div className="h-64 rounded-3xl border border-stone-200 bg-stone-100/60" />
              <div className="h-56 rounded-3xl border border-stone-200 bg-stone-100/60" />
              <div className="h-72 rounded-3xl border border-stone-200 bg-stone-100/60" />
            </div>
            <div className="h-72 rounded-3xl border border-stone-200 bg-stone-100/60 md:w-[340px]" />
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="configurator" className="bg-white">
      <div className="mx-auto max-w-[1200px] px-5 py-14 md:px-10 md:py-20">
        {/* Section header */}
        <div className="mb-2 flex items-center justify-center gap-2">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-stone-200" />
          <span className="text-xs font-semibold uppercase tracking-widest text-home-orange_1">
            {t("trip-details-section-title")}
          </span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-stone-200" />
        </div>
        <h2 className="mb-3 text-center text-3xl font-bold tracking-tight text-stone-900 md:text-4xl">
          {t("trip-details-heading-generic")}
        </h2>
        <p className="mx-auto mb-10 max-w-2xl text-center text-base leading-relaxed text-stone-600">
          {t("trip-details-description")}
        </p>

        <div className="flex flex-col gap-8 md:flex-row">
          {/* ========== Left — Configurator ========== */}
          <div className="flex-1">
            <div className="space-y-5">
              {/* ─── Step 1: Countries ─── */}
              <div
                className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6"
                style={{
                  boxShadow:
                    "0 16px 40px -12px rgba(28,25,23,0.18), 0 4px 12px -4px rgba(28,25,23,0.08)",
                }}
              >
                <div className="mb-4 flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-home-orange_1 text-sm font-bold text-white ring-4 ring-orange-50 shadow-[0_4px_10px_-2px_rgba(234,88,12,0.4)]">
                    1
                  </span>
                  <div>
                    <span className="text-base font-semibold text-stone-900">
                      {t("step-destination-title")}
                    </span>
                    <span className="ml-2 text-xs text-stone-600">
                      {t("step-destination-hint")}
                    </span>
                  </div>
                </div>

                {/* Selected country pills */}
                <div className="mb-3 flex flex-wrap gap-2">
                  {Array.from(selectedIso2s).map((iso2) => (
                    <span
                      key={iso2}
                      className="group inline-flex items-center gap-1.5 rounded-full border border-home-orange_1/20 bg-home-orange_1/5 px-3 py-1.5 text-sm font-medium text-home-orange_1 transition-colors hover:bg-home-orange_1/10"
                    >
                      <Image src={getFlagUrl(iso2)} alt="" width={14} height={14} className="inline-block rounded-[1px] object-cover" />
                      {countryNameMap.get(iso2) || iso2}
                      {selectedIso2s.size > 1 && (
                        <button
                          onClick={() => toggleCountry(iso2)}
                          className="ml-0.5 rounded-full p-0.5 opacity-60 transition-opacity hover:opacity-100"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </button>
                      )}
                    </span>
                  ))}
                  <button
                    onClick={() => setShowCountryPicker(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-stone-300 px-3.5 py-1.5 text-sm font-medium text-stone-600 transition-all hover:border-home-orange_1 hover:bg-orange-50 hover:text-home-orange_1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    {t("step-add-country")}
                  </button>
                </div>

                {/* Quick presets */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
                    {t("step-quick-label")}
                  </span>
                  {(countryPresets ?? [
                    { label: countryName, iso2s: [...baseIso2s] },
                    { label: t("step-preset-korea", { countryName }), iso2s: [...baseIso2s, "KR"] },
                    { label: t("step-preset-singapore", { countryName }), iso2s: [...baseIso2s, "SG"] },
                    { label: t("step-preset-multi-asia"), iso2s: [...baseIso2s, "KR", "TH", "SG", "TW", "HK"] },
                  ]).map((preset) => {
                    const isActive =
                      preset.iso2s.length === selectedIso2s.size &&
                      preset.iso2s.every((c) => selectedIso2s.has(c))
                    const className = cn(
                      "rounded-full px-2.5 py-1 text-[11px] transition-all",
                      isActive
                        ? "bg-home-orange_1/10 font-semibold text-home-orange_1"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200 hover:text-stone-900"
                    )

                    if (preset.href) {
                      return (
                        <Link
                          key={preset.label}
                          href={preset.href}
                          className={className}
                        >
                          {preset.label}
                        </Link>
                      )
                    }

                    return (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => applyPreset(preset.iso2s)}
                        className={className}
                      >
                        {preset.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* ─── Step 2: Days ─── */}
              <div
                className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6"
                style={{
                  boxShadow:
                    "0 16px 40px -12px rgba(28,25,23,0.18), 0 4px 12px -4px rgba(28,25,23,0.08)",
                }}
              >
                <div className="mb-4 flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-home-orange_1 text-sm font-bold text-white ring-4 ring-orange-50 shadow-[0_4px_10px_-2px_rgba(234,88,12,0.4)]">
                    2
                  </span>
                  <div>
                    <span className="text-base font-semibold text-stone-900">
                      {t("step-duration-title")}
                    </span>
                    <span className="ml-2 text-xs text-stone-600">
                      {t("step-duration-hint")}
                    </span>
                  </div>
                </div>

                {/* Quick preset chips */}
                <div className="mb-4 flex flex-wrap gap-2">
                  {DAY_PRESETS.map((d) => (
                    <button
                      key={d}
                      onClick={() => { quotaManualRef.current = false; trackTripDurationChange(d, "preset"); setDays(d) }}
                      className={cn(
                        "rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all",
                        !isLongTerm && days === d
                          ? "bg-home-orange_1 text-white shadow-[0_0_0_3px_rgba(234,88,12,0.15),0_4px_12px_-4px_rgba(234,88,12,0.5)]"
                          : "border border-stone-200 bg-stone-50 text-stone-700 hover:border-orange-300 hover:bg-stone-100 hover:text-orange-700"
                      )}
                    >
                      {d === 1 ? t("step-days-unit-singular", { count: d }) : t("step-days-unit", { count: d })}
                    </button>
                  ))}
                  {/* Long-term entry chip */}
                  <button
                    onClick={() => { if (!isLongTerm) { quotaManualRef.current = false; trackTripDurationChange(31, "long_term_preset"); setDays(31) } }}
                    className={cn(
                      "rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all",
                      isLongTerm
                        ? "bg-home-orange_1 text-white shadow-[0_0_0_3px_rgba(234,88,12,0.15),0_4px_12px_-4px_rgba(234,88,12,0.5)]"
                        : "border border-dashed border-stone-300 bg-stone-50 text-stone-600 hover:border-orange-300 hover:bg-stone-100 hover:text-orange-700"
                    )}
                  >
                    {t("step-30plus-days")}
                  </button>
                </div>

                {/* Short-term: Slider on top, stepper below */}
                {!isLongTerm && (
                  <div>
                    {/* Range slider — desktop only */}
                    <div className="mb-3 hidden md:block">
                      <div className="relative h-8">
                        <div className="pointer-events-none absolute left-0 top-1/2 h-2 w-full -translate-y-1/2 rounded-full bg-stone-200" />
                        <div
                          className="pointer-events-none absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-home-orange_1"
                          style={{ width: `${sliderPercent}%` }}
                        />
                        <input
                          type="range"
                          min={MIN_DAYS}
                          max={SLIDER_MAX}
                          value={Math.min(days, SLIDER_MAX)}
                          onChange={(e) => { const nextDays = Number(e.target.value); quotaManualRef.current = false; trackTripDurationChange(nextDays, "slider"); setDays(nextDays) }}
                          className={RANGE_SLIDER_CLASS_NAME}
                        />
                      </div>
                      {/* Scale marks */}
                      <div className="mt-0.5 flex justify-between px-0.5">
                        <span className="text-[10px] font-medium text-stone-500">1</span>
                        <span className="text-[10px] font-medium text-stone-500">10</span>
                        <span className="text-[10px] font-medium text-stone-500">20</span>
                        <span className="text-[10px] font-medium text-stone-500">30</span>
                      </div>
                    </div>

                    {/* Numeric stepper: [−] [value] [unit] [+] */}
                    <div className="flex items-center justify-center gap-2 md:justify-end">
                      <button
                        type="button"
                        aria-label={t("stepper-days-decrease")}
                        onClick={() => adjustDays(-1)}
                        disabled={days <= MIN_DAYS}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-300 bg-white text-stone-700 transition-colors hover:border-orange-300 hover:bg-stone-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-30 md:h-10 md:w-10"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={daysInput}
                          onFocus={(e) => { daysInputFocused.current = true; e.target.select() }}
                          onChange={(e) => { if (/^\d*$/.test(e.target.value)) setDaysInput(e.target.value) }}
                          onBlur={() => {
                            daysInputFocused.current = false
                            const v = parseInt(daysInput, 10)
                            if (isNaN(v) || v < MIN_DAYS) { setDaysClamped(MIN_DAYS); setDaysInput(String(MIN_DAYS)) }
                            else if (v > SLIDER_MAX) { setDaysClamped(SLIDER_MAX); setDaysInput(String(SLIDER_MAX)) }
                            else { setDaysClamped(v) }
                          }}
                          className="w-16 rounded-xl border border-stone-200 bg-white py-2 text-center text-3xl font-bold tabular-nums text-home-orange_1 outline-none transition-colors focus:border-home-orange_1"
                        />
                        <span className="text-sm font-semibold text-stone-700">
                          {t("step-days-label")}
                        </span>
                      </div>
                      <button
                        type="button"
                        aria-label={t("stepper-days-increase")}
                        onClick={() => adjustDays(1)}
                        disabled={days >= SLIDER_MAX}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-300 bg-white text-stone-700 transition-colors hover:border-orange-300 hover:bg-stone-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-30 md:h-10 md:w-10"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v14" />
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Long-term: input only, no slider */}
                {isLongTerm && (
                  <div>
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        aria-label={t("stepper-days-decrease-5")}
                        onClick={() => adjustDays(-5)}
                        disabled={days <= SLIDER_MAX + 1}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-300 bg-white text-stone-700 transition-colors hover:border-orange-300 hover:bg-stone-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-30 md:h-10 md:w-10"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={daysInput}
                          onFocus={(e) => { daysInputFocused.current = true; e.target.select() }}
                          onChange={(e) => { if (/^\d*$/.test(e.target.value)) setDaysInput(e.target.value) }}
                          onBlur={() => {
                            daysInputFocused.current = false
                            const v = parseInt(daysInput, 10)
                            if (isNaN(v) || v < SLIDER_MAX + 1) { setDaysClamped(SLIDER_MAX + 1); setDaysInput(String(SLIDER_MAX + 1)) }
                            else if (v > MAX_DAYS) { setDaysClamped(MAX_DAYS); setDaysInput(String(MAX_DAYS)) }
                            else { setDaysClamped(v) }
                          }}
                          className="w-20 rounded-xl border border-stone-200 bg-white py-2 text-center text-3xl font-bold tabular-nums text-home-orange_1 outline-none transition-colors focus:border-home-orange_1"
                        />
                        <span className="text-sm font-semibold text-stone-700">
                          {t("step-days-label")}
                        </span>
                      </div>
                      <button
                        type="button"
                        aria-label={t("stepper-days-increase-5")}
                        onClick={() => adjustDays(5)}
                        disabled={days >= MAX_DAYS}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-300 bg-white text-stone-700 transition-colors hover:border-orange-300 hover:bg-stone-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-30 md:h-10 md:w-10"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v14" />
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                    </div>
                    <p className="mt-2 text-center text-xs text-stone-600">
                      {t("step-long-term-hint")}
                    </p>
                  </div>
                )}
              </div>

              {/* ─── Step 3: Data — two-layer selection ─── */}
              <div
                className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6"
                style={{
                  boxShadow:
                    "0 16px 40px -12px rgba(28,25,23,0.18), 0 4px 12px -4px rgba(28,25,23,0.08)",
                }}
              >
                <div className="mb-4 flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-home-orange_1 text-sm font-bold text-white ring-4 ring-orange-50 shadow-[0_4px_10px_-2px_rgba(234,88,12,0.4)]">
                    3
                  </span>
                  <div>
                    <span className="text-base font-semibold text-stone-900">
                      {t("step-data-plan-title")}
                    </span>
                    <span className="ml-2 text-xs text-stone-600">
                      {probing
                        ? t("step-data-checking-options")
                        : t("step-data-options-hint")}
                    </span>
                  </div>
                  {probing && (
                    <div className="ml-auto h-4 w-4 animate-spin rounded-full border-2 border-stone-300 border-t-home-orange_1" />
                  )}
                </div>

                <div className={cn(probing && "pointer-events-none opacity-50")}>
                {/* Layer 1: Data Mode */}
                <div className="mb-4">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                    {t("step-data-mode-label")}
                  </p>
                  <div className="inline-flex rounded-xl border border-stone-200 bg-stone-100 p-1">
                    {([
                      { mode: "total" as DataMode, label: "data-mode-total-or-unlimited" },
                      { mode: "daily" as DataMode, label: "data-mode-daily" },
                    ]).map((opt) => (
                      <button
                        key={opt.mode}
                        onClick={() => {
                          preserveInitialQuotaRef.current = false
                          trackTripQuotaChange(apiParams.quota, opt.mode === "daily" ? dynamicDailyQuotas[Math.min(dailyDataIdx, dynamicDailyQuotas.length - 1)]?.value : totalQuotaMB, "data_mode"); setDataMode(opt.mode)
                        }}
                        className={cn(
                          "min-h-10 rounded-lg px-5 text-sm font-semibold transition-all",
                          dataMode === opt.mode
                            ? "bg-home-orange_1 text-white shadow-[0_2px_8px_-2px_rgba(234,88,12,0.4)]"
                            : "text-stone-600 hover:text-stone-900"
                        )}
                      >
                        {t(opt.label)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Usage guide — help users who don't know how much data */}
                <details className="group mb-4">
                  <summary className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-home-orange_1 hover:underline">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 transition-transform group-open:rotate-90">
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                    {t("step-data-guide")}
                  </summary>
                  <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                    {[
                      { key: "light", gb: 3 },
                      { key: "moderate", gb: 7 },
                      { key: "heavy", gb: 15 },
                    ].map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setQuotaManual(item.gb * 1024, "usage_guide")}
                        className="rounded-xl border border-stone-200 bg-white px-3 py-2 text-left transition-colors hover:border-orange-300 hover:bg-orange-50/40"
                      >
                        <p className="text-xs font-semibold text-stone-800">{t(`usage-guide-${item.key}`)}</p>
                        <p className="text-[10px] text-stone-600">{t(`usage-guide-${item.key}-desc`)}</p>
                        <p className="mt-1 text-[10px] font-semibold text-home-orange_1">
                          {dataMode === "daily" ? t(`usage-guide-${item.key}-daily`) : t(`usage-guide-${item.key}-total`)}
                        </p>
                      </button>
                    ))}
                  </div>
                </details>

                {/* Layer 2: Amount (conditional) */}
                {dataMode === "total" && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                        {t("total-data-amount-label")}
                      </p>
                      <p className="text-[10px] text-stone-600">
                        {t("total-data-preset-hint")}
                      </p>
                    </div>

                    {/* Quick preset chips — uses dynamic probe results + Unlimited */}
                    <div className="mb-4 flex flex-wrap gap-2">
                      {[...visibleTotalQuotaOptions.filter((o) => o.value > 0), { label: t("configurator-data-unlimited"), value: -1 }].map((opt) => {
                        const isUl = opt.value === -1
                        const active = isUl ? isUnlimited : (!isUnlimited && totalQuotaMB === opt.value)
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setQuotaManual(opt.value, "preset")}
                            className={cn(
                              "rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all",
                              active
                                ? isUl
                                  ? "bg-purple-600 text-white shadow-[0_0_0_3px_rgba(147,51,234,0.15),0_4px_12px_-4px_rgba(147,51,234,0.5)]"
                                  : "bg-home-orange_1 text-white shadow-[0_0_0_3px_rgba(234,88,12,0.15),0_4px_12px_-4px_rgba(234,88,12,0.5)]"
                                : "border border-stone-200 bg-stone-50 text-stone-700 hover:border-orange-300 hover:bg-stone-100 hover:text-orange-700"
                            )}
                          >
                            {opt.label}
                          </button>
                        )
                      })}
                    </div>

                    {/* Slider on top, stepper below — hidden only for Unlimited */}
                    {!isUnlimited && (
                      <div>
                        {/* Range slider — desktop only */}
                        <div className="mb-3 hidden md:block">
                          <div className="relative h-8">
                            <div className="pointer-events-none absolute left-0 top-1/2 h-2 w-full -translate-y-1/2 rounded-full bg-stone-200" />
                            <div
                              className="pointer-events-none absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-home-orange_1"
                              style={{ width: `${((totalQuotaGB - MIN_QUOTA_GB) / (MAX_QUOTA_GB - MIN_QUOTA_GB)) * 100}%` }}
                            />
                            <input
                              type="range"
                              min={MIN_QUOTA_GB}
                              max={MAX_QUOTA_GB}
                              value={totalQuotaGB}
                              onChange={(e) => setQuotaManual(Number(e.target.value) * 1024)}
                              className={RANGE_SLIDER_CLASS_NAME}
                            />
                          </div>
                          <div className="mt-0.5 flex justify-between px-0.5">
                            <span className="text-[10px] font-medium text-stone-500">1</span>
                            <span className="text-[10px] font-medium text-stone-500">10</span>
                            <span className="text-[10px] font-medium text-stone-500">25</span>
                            <span className="text-[10px] font-medium text-stone-500">50</span>
                          </div>
                        </div>

                        {/* Numeric stepper: [−] [value] [unit] [+] */}
                        <div className="flex items-center justify-center gap-2 md:justify-end">
                          <button
                            type="button"
                            aria-label={t("stepper-quota-decrease")}
                            onClick={() => setQuotaManual(Math.max(MIN_QUOTA_GB, totalQuotaGB - 1) * 1024, "stepper")}
                            disabled={totalQuotaGB <= MIN_QUOTA_GB}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-300 bg-white text-stone-700 transition-colors hover:border-orange-300 hover:bg-stone-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-30 md:h-10 md:w-10"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12h14" />
                            </svg>
                          </button>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={MIN_QUOTA_GB}
                              max={MAX_QUOTA_GB}
                              value={totalQuotaGB}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10)
                                if (!isNaN(v)) setQuotaManual(Math.max(MIN_QUOTA_GB, Math.min(MAX_QUOTA_GB, v)) * 1024, "input")
                              }}
                              className="w-16 rounded-xl border border-stone-200 bg-white py-2 text-center text-3xl font-bold tabular-nums text-home-orange_1 outline-none transition-colors focus:border-home-orange_1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <span className="text-sm font-semibold text-stone-700">
                              GB
                            </span>
                          </div>
                          <button
                            type="button"
                            aria-label={t("stepper-quota-increase")}
                            onClick={() => setQuotaManual(Math.min(MAX_QUOTA_GB, totalQuotaGB + 1) * 1024, "stepper")}
                            disabled={totalQuotaGB >= MAX_QUOTA_GB}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-300 bg-white text-stone-700 transition-colors hover:border-orange-300 hover:bg-stone-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-30 md:h-10 md:w-10"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 5v14" />
                              <path d="M5 12h14" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Current value hint */}
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-[11px] text-stone-600">
                        {isUnlimited
                          ? t("total-data-unlimited-hint")
                          : t("total-data-slider-hint")}
                      </p>
                      <span className={cn(
                        "shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold",
                        isUnlimited
                          ? "bg-purple-100 text-purple-700"
                          : "bg-home-orange_1/10 text-home-orange_1"
                      )}>
                        {isUnlimited
                          ? t("total-data-unlimited-label")
                          : `${totalQuotaGB} GB`}
                      </span>
                    </div>
                  </div>
                )}

                {dataMode === "daily" && (
                  <div>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                      {t("daily-data-amount-label")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {dynamicDailyQuotas.map((opt, i) => (
                        <button
                          key={opt.value}
                          onClick={() => { trackTripQuotaChange(apiParams.quota, opt.value, "daily_preset"); setDailyDataIdx(i) }}
                          className={cn(
                            "rounded-full px-4 py-2 text-sm font-semibold transition-all",
                            dailyDataIdx === i
                              ? "bg-home-orange_1 text-white shadow-[0_0_0_3px_rgba(234,88,12,0.15),0_4px_12px_-4px_rgba(234,88,12,0.5)]"
                              : "border border-stone-200 bg-stone-50 text-stone-700 hover:border-orange-300 hover:bg-stone-100 hover:text-orange-700"
                          )}
                        >
                          {opt.value === -1 ? t("configurator-data-unlimited") : opt.labelEN}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-stone-600">
                      {t("daily-data-reset-hint")}
                    </p>
                  </div>
                )}

                </div>{/* end probing wrapper */}
              </div>
            </div>

            {/* ========== Mobile-only CTA strip ========== */}
            <div className="mt-4 md:hidden">
              <div
                className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3"
                style={{
                  boxShadow:
                    "0 8px 24px -8px rgba(28,25,23,0.12), 0 2px 6px -2px rgba(28,25,23,0.06)",
                }}
              >
                <div className="min-w-0 flex-1 text-xs text-stone-600">
                  {loading ? (
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-stone-300 border-t-home-orange_1" />
                      {t("summary-searching")}
                    </span>
                  ) : displayedVisibleOfferCount > 0 ? (
                    <span className="font-semibold text-stone-700">{t("configurator-results-count", { count: displayedVisibleOfferCount })}</span>
                  ) : (
                    <span className="text-stone-600">{t("summary-no-match")}</span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={loading || displayedVisibleOfferCount === 0}
                  onClick={() => {
                    const target = document.getElementById("generated-plans")
                    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" })
                  }}
                  className={cn(
                    "h-11 shrink-0 rounded-full px-5 text-sm font-semibold transition-colors",
                    loading || displayedVisibleOfferCount === 0
                      ? "cursor-not-allowed bg-stone-200 text-stone-400"
                      : "bg-home-orange_1 text-white hover:bg-orange-700"
                  )}
                  style={
                    loading || displayedVisibleOfferCount === 0
                      ? undefined
                      : { boxShadow: "0 8px 18px -8px rgba(234,88,12,0.55)" }
                  }
                >
                  {loading
                    ? t("summary-button-searching")
                    : displayedVisibleOfferCount === 0
                    ? t("summary-button-no-results")
                    : t("summary-button-view-recommendations")}
                </button>
              </div>
            </div>
          </div>

          {/* ========== Right — Summary Card (desktop only) ========== */}
          <div className="hidden md:block md:w-[340px]">
            <div
              className="sticky top-24 overflow-hidden rounded-3xl border border-stone-200 bg-white"
              style={{
                boxShadow:
                  "0 16px 40px -12px rgba(28,25,23,0.18), 0 4px 12px -4px rgba(28,25,23,0.08)",
              }}
            >
              {/* Card header */}
              <div className="border-b border-stone-200/60 px-6 py-4">
                <p className="text-base font-semibold text-stone-900">
                  {t("summary-panel-title")}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-stone-600">
                  {t("summary-panel-subtitle")}
                </p>
                <span className="mt-3 inline-flex h-6 items-center gap-1.5 rounded-full bg-orange-100 px-2.5 text-[11px] font-semibold text-orange-700">
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12 14 4-4" />
                    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
                  </svg>
                  {selectedCountryNames.length}
                  {" · "}
                  {days === 1 ? t("step-days-unit-singular", { count: days }) : t("step-days-unit", { count: days })}
                  {" · "}
                  {summaryDataLabel}
                </span>
              </div>

              <div className="p-5">
                {/* ── Section 1: Input confirmation ── */}
                <div className="space-y-2.5">
                  {/* Countries */}
                  <div className="flex items-start gap-3 rounded-xl bg-stone-50 px-3.5 py-2.5">
                    <svg className="mt-0.5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#57534e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    <div className="min-w-0">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                        {t("summary-countries-label")}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {selectedCountryNames.map((name, i) => (
                          <span key={i} className="text-[13px] font-semibold text-stone-900">
                            {name}{i < selectedCountryNames.length - 1 ? listSep(lng) : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Days */}
                  <div className="flex items-start gap-3 rounded-xl bg-stone-50 px-3.5 py-2.5">
                    <svg className="mt-0.5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#57534e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                      <path d="M16 2v4" />
                      <path d="M8 2v4" />
                      <path d="M3 10h18" />
                    </svg>
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                        {t("summary-duration-label")}
                      </span>
                      <div className="text-[13px] font-semibold text-stone-900">
                        {days === 1 ? t("step-days-unit-singular", { count: days }) : t("step-days-unit", { count: days })}
                      </div>
                    </div>
                  </div>

                  {/* Data */}
                  <div className="flex items-start gap-3 rounded-xl bg-stone-50 px-3.5 py-2.5">
                    <svg className="mt-0.5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#57534e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <ellipse cx="12" cy="5" rx="9" ry="3" />
                      <path d="M3 5V19A9 3 0 0 0 21 19V5" />
                      <path d="M3 12A9 3 0 0 0 21 12" />
                    </svg>
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                        {t("summary-data-label")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-stone-900">
                          {summaryDataLabel}
                        </span>
                        <span className={cn(
                          "rounded px-1.5 py-0.5 text-[10px] font-semibold",
                          dataMode === "unlimited" || apiParams.quota === -1
                            ? "bg-purple-100 text-purple-700"
                            : dataMode === "daily"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-stone-100 text-stone-700"
                        )}>
                          {summaryModeLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Divider ── */}
                <div className="my-4 border-t border-dashed border-stone-200" />

                {/* ── Section 2: Result preview ── */}
                <div>
                  <p className="mb-2.5 text-xs font-semibold text-stone-700">
                    {t("summary-what-next")}
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-50">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      </span>
                      <span className="text-xs leading-relaxed text-stone-700">
                        {t("summary-what-next-best-match")}
                      </span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-50">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
                          <polyline points="16 17 22 17 22 11" />
                        </svg>
                      </span>
                      <span className="text-xs leading-relaxed text-stone-700">
                        {t("summary-what-next-cheaper")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── Section 3: Result count + live lowest price ── */}
                <div className="mt-4 rounded-xl bg-orange-50/60 px-4 py-3">
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-stone-300 border-t-home-orange_1" />
                      <span className="text-xs text-stone-600">
                        {t("summary-searching")}
                      </span>
                    </div>
                  ) : displayedVisibleOfferCount > 0 ? (
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-stone-700">
                        {dualResult.exactMatches.length === 0 && dualResult.nearMatches.length > 0
                          ? t("summary-no-exact-found-closest", { days, count: displayedVisibleOfferCount })
                          : hasMultipleVisibleResults
                          ? t("summary-found-multiple", { count: displayedVisibleOfferCount })
                          : hasSingleVisibleResult
                          ? t("summary-found-match")
                          : t("summary-no-exact-found-closest", { days, count: displayedVisibleOfferCount })}
                      </p>
                      {lowestFormatted && (
                        <p className="flex items-baseline gap-1 text-stone-500">
                          <span className="text-[11px]">
                            {t("summary-from-price-label")}
                          </span>
                          <span className="text-base font-bold tabular-nums text-home-orange_1">
                            {lowestFormatted}
                          </span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-stone-600">
                        {t("summary-no-match")}
                      </p>
                      <p className="text-[11px] text-stone-600">
                        {t("summary-no-match-hint")}
                      </p>
                    </div>
                  )}
                </div>

                {/* Single primary CTA */}
                <button
                  type="button"
                  disabled={loading || displayedVisibleOfferCount === 0}
                  onClick={() => {
                    const target = document.getElementById("generated-plans")
                    if (target) {
                      target.scrollIntoView({ behavior: "smooth", block: "start" })
                      target.classList.add("summary-card-highlight")
                      setTimeout(() => target.classList.remove("summary-card-highlight"), 2000)
                    }
                  }}
                  className={cn(
                    "mt-4 block h-12 w-full rounded-full text-center text-sm font-semibold transition-colors",
                    loading || displayedVisibleOfferCount === 0
                      ? "cursor-not-allowed bg-stone-300 text-stone-500"
                      : "bg-home-orange_1 text-white hover:bg-orange-700"
                  )}
                  style={
                    loading || displayedVisibleOfferCount === 0
                      ? undefined
                      : { boxShadow: "0 8px 18px -8px rgba(234,88,12,0.55)" }
                  }
                >
                  {loading
                    ? t("summary-button-searching")
                    : displayedVisibleOfferCount === 0
                      ? t("summary-button-no-results")
                      : t("summary-button-view-recommendations")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ========== Crawlable summary (for search engines) ========== */}
        <p className="sr-only" aria-hidden="false">
          {t("results-seo-summary", {
            countries: selectedCountryNames.join(", "),
            days,
            daysUnit: days === 1 ? t("results-seo-summary-days-unit-singular") : t("results-seo-summary-days-unit"),
            data: summaryDataLabel
          })}
        </p>

        {/* ========== Three-Tier Results ========== */}
        {displayedVisibleOfferCount > 0 && (
          <div id="generated-plans" className="mt-14 scroll-mt-24 rounded-2xl transition-shadow duration-500">
            {/* Compact header: title with inline count + need pills + "modify" link.
                Dropped the gray explanation strip and the redundant "你的需求:" label —
                the count tells you how many, the pills tell you what was asked. */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-stone-900">
                  {t("results-section-title-with-count", { count: displayedVisibleOfferCount })}
                </h3>
                <a
                  href="#configurator"
                  className="shrink-0 text-xs text-home-orange_1 transition-colors hover:underline"
                >
                  {t("results-modify-button")}
                </a>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-700">
                  {selectedCountryNames.join(listSep(lng))}
                </span>
                <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-700">
                  {days === 1 ? t("step-days-unit-singular", { count: days }) : t("step-days-unit", { count: days })}
                </span>
                <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-700">
                  {summaryDataLabel}
                </span>
              </div>
              <div className="mt-3 flex items-start gap-2.5 rounded-2xl border border-orange-200/60 bg-orange-50/40 p-3.5 md:gap-3 md:p-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
                <p className="text-xs leading-relaxed text-stone-700 md:text-sm">
                  {t("results-multi-operator-note")}
                </p>
              </div>
              {showResultsRefreshing && (
                <div
                  aria-live="polite"
                  className="mt-3 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
                >
                  <div className="mt-0.5 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-amber-200 border-t-amber-500" />
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-amber-800">
                      {t("results-refreshing-title")}
                    </p>
                    <p className="text-xs leading-relaxed text-amber-700">
                      {t("results-refreshing-body", { request: requestSummaryLabel })}
                    </p>
                  </div>
                </div>
              )}
              {showResultsUpdated && !loading && (
                <div
                  aria-live="polite"
                  className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <p className="text-xs font-medium text-emerald-700">
                    {t("results-updated", { count: displayedVisibleOfferCount })}
                  </p>
                </div>
              )}
            </div>

            <div className={cn("transition-opacity duration-200", showResultsRefreshing ? "pointer-events-none opacity-60" : "opacity-100")}>
            {/* Single-country: flat list of all matching SKUs + cheapest unlimited on top (Flutter-aligned).
                Uses the same helpers as getRenderableOfferCount so header count === card count. */}
            {(() => {
              if (displayedIso2s.length !== 1) return null

              const userIso = displayedIso2s[0].toUpperCase()
              const candidates = buildFlatSingleCountryCandidates(displayedSmartCategories, displayedDualResult, userIso)
              const userQuota = apiParams.quota
              const userDay = days
              // 默认按 fingerprint (operators + features) 去重，max 4-6 张（Flutter 对齐）
              const ordered = computeFlatSingleCountryOrdered(candidates, userDay, userQuota, {
                userIso,
                expanded: showAllFlatPlans,
              })
              // 展开版（不去重）用于显示 toggle 上的数字（"查看全部 N 个"）+ 展开后渲染
              const expandedOrdered = computeFlatSingleCountryOrdered(candidates, userDay, userQuota, {
                userIso,
                expanded: true,
              })
              const hiddenCount = Math.max(0, expandedOrdered.length - ordered.length)

              if (ordered.length === 0) return null

              // Derived view of finite-only cards (preserves sort order from `ordered`),
              // used to compute the "省 $X" badge on the cheapest finite card.
              const finite = ordered.filter((p) => !isUnlimitedSmartPlan(p))
              const cheapestFinite = finite[0] ?? null
              // 最便宜的 unlimited（"最推荐"紫色 banner 唯一获得者）。
              // ordered 内部已按价格升序，第一个 unlimited 就是最便宜的。
              const cheapestUnlimited = ordered.find((p) => isUnlimitedSmartPlan(p)) ?? null
              const isZh = (lng || "").toLowerCase().startsWith("zh")

              return (
                <div className="mb-8">
                  {/* No sub-header — the section title above already says "N plans for you" */}
                  <div
                    className={cn(
                      "grid grid-cols-1 gap-5",
                      ordered.length >= 4
                        ? "md:grid-cols-2 xl:grid-cols-4 xl:items-stretch"
                        : ordered.length === 3
                          ? "md:grid-cols-3 md:items-stretch"
                          : "md:grid-cols-2 md:items-stretch"
                    )}
                  >
                    {ordered.map((plan, idx) => {
                      const key = planKey(plan)
                      const unlim = isUnlimitedSmartPlan(plan)
                      const isCheapestFinite = !unlim && plan === cheapestFinite
                      const upgradePerDayCents = unlim && cheapestFinite && plan.data.day > 0
                        ? Math.round((plan.amount - cheapestFinite.amount) / plan.data.day)
                        : undefined
                      const isSelected = displayedSelectedPlanKey === key

                      // Role 4-color scheme: 紫色"最推荐"仅给最便宜 unlimited；其他 unlimited 中性紫"升级版"；
                      //   cheapest finite 绿色"最佳价"；其他 finite 灰色"其他选择"。
                      // 这样保证整页只有 1 张紫色高饱和 banner，用户视觉聚焦清晰。
                      type FlatRole = "unlimited-pick" | "unlimited-alt" | "cheapest" | "alternative"
                      const isCheapestUnlimited = unlim && plan === cheapestUnlimited
                      const role: FlatRole = isCheapestUnlimited
                        ? "unlimited-pick"
                        : unlim
                          ? "unlimited-alt"
                          : isCheapestFinite
                            ? "cheapest"
                            : "alternative"

                      const ROLE: Record<FlatRole, {
                        bannerBg: string
                        bannerText: string
                        bannerIcon: React.ReactNode
                        bannerLabel: string
                        cardBorderIdle: string
                        cardBorderSelected: string
                      }> = {
                        "unlimited-pick": {
                          bannerBg: "bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-500",
                          bannerText: "text-white",
                          bannerIcon: (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.739-8z" />
                            </svg>
                          ),
                          bannerLabel: t("recommendation-pill-most-recommended"),
                          cardBorderIdle: "border border-violet-300",
                          cardBorderSelected: "border-[3px] border-violet-600 shadow-2xl shadow-violet-500/40 -translate-y-0.5",
                        },
                        "unlimited-alt": {
                          // 其他 unlimited：中性紫，banner 不抢戏（淡紫底 + 紫字，与 pick 形成明显层级）
                          bannerBg: "bg-violet-100",
                          bannerText: "text-violet-700",
                          bannerIcon: (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.739-8z" />
                            </svg>
                          ),
                          bannerLabel: t("recommendation-pill-unlimited-alt"),
                          cardBorderIdle: "border border-violet-100",
                          cardBorderSelected: "border-[3px] border-violet-400 shadow-xl shadow-violet-300/30 -translate-y-0.5",
                        },
                        cheapest: {
                          bannerBg: "bg-emerald-50",
                          bannerText: "text-emerald-700",
                          bannerIcon: (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <line x1="12" y1="1" x2="12" y2="23" />
                              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                            </svg>
                          ),
                          bannerLabel: t("recommendation-pill-cheapest"),
                          cardBorderIdle: "border border-emerald-200",
                          cardBorderSelected: "border-[3px] border-emerald-500 shadow-xl shadow-emerald-300/30 -translate-y-0.5",
                        },
                        alternative: {
                          bannerBg: "bg-stone-100",
                          bannerText: "text-stone-600",
                          bannerIcon: (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                          ),
                          bannerLabel: t("recommendation-pill-alternative"),
                          cardBorderIdle: "border border-stone-200",
                          cardBorderSelected: "border-[3px] border-stone-500 shadow-xl shadow-stone-300/30 -translate-y-0.5",
                        },
                      }

                      // 5 core feature chips, mirrored from Flutter — always rendered for visibility,
                      // with reduced opacity + strikethrough when the plan does NOT have the feature.
                      type FeatureChip = { key: string; label: string; on: boolean; bgOn: string; textOn: string; iconOn: React.ReactNode }
                      const tags = plan.tags || ({} as Record<string, unknown>)
                      const featureChips: FeatureChip[] = [
                        {
                          key: "popular",
                          label: t("plan-tag-popular"),
                          on: !!(tags as { promotion?: boolean; popular?: boolean }).promotion || !!(tags as { popular?: boolean }).popular,
                          bgOn: "bg-orange-50",
                          textOn: "text-orange-600",
                          iconOn: (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z" /></svg>),
                        },
                        {
                          key: "phone",
                          label: t("plan-tag-with-phone-number"),
                          on: !!(tags as { phoneNumber?: boolean }).phoneNumber,
                          bgOn: "bg-purple-50",
                          textOn: "text-purple-700",
                          iconOn: (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z" /></svg>),
                        },
                        {
                          key: "topup",
                          label: t("plan-tag-can-topup"),
                          on: !!(tags as { topup?: boolean }).topup,
                          bgOn: "bg-sky-50",
                          textOn: "text-sky-700",
                          iconOn: (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
                        },
                        {
                          key: "hotspot",
                          label: t("plan-tag-hotspot"),
                          on: !!(tags as { hotspot?: boolean }).hotspot,
                          bgOn: "bg-cyan-50",
                          textOn: "text-cyan-700",
                          iconOn: (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12.55a11 11 0 0114.08 0" /><path d="M1.42 9a16 16 0 0121.16 0" /><path d="M8.53 16.11a6 6 0 016.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" /></svg>),
                        },
                        {
                          key: "yoho-care",
                          label: t("plan-tag-yoho-care"),
                          on: !!(tags as { yohoCare?: boolean }).yohoCare,
                          bgOn: "bg-emerald-50",
                          textOn: "text-emerald-700",
                          iconOn: (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>),
                        },
                      ]

                      // "省 $X" badge: only on the cheapest finite card, comparing to the next finite SKU.
                      const nextFinitePrice = isCheapestFinite && finite.length > 1
                        ? finite[1].amount
                        : null
                      const savingsBadge = isCheapestFinite && nextFinitePrice && nextFinitePrice > plan.amount
                        ? t("recommendation-save-amount", { amount: ((nextFinitePrice - plan.amount) / 100).toFixed(2) })
                        : null

                      const cfg = ROLE[role]

                      return (
                        <div
                          key={`flat_${plan.provider}_${plan.data.quota}_${plan.data.day}_${idx}`}
                          className={cn(
                            "relative flex flex-col rounded-2xl overflow-hidden bg-white transition-all duration-200",
                            isSelected ? cfg.cardBorderSelected : cfg.cardBorderIdle,
                            !isSelected && "hover:shadow-md"
                          )}
                        >
                          {/* Full-width role banner — never overlapped by selection ring */}
                          <div className={cn(
                            "flex items-center gap-2 px-4 py-2",
                            cfg.bannerBg, cfg.bannerText
                          )}>
                            {cfg.bannerIcon}
                            <span className="text-[12px] font-semibold tracking-wide">{cfg.bannerLabel}</span>
                            {savingsBadge && (
                              <span className="ml-auto rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-bold backdrop-blur-sm">
                                {savingsBadge}
                              </span>
                            )}
                          </div>

                          {/* PlanCard renders price/network/CTA. Tags + selection chip suppressed; we render our own below. */}
                          <PlanCard
                            plan={{ ...plan, alternativeProviders: [] }}
                            iso2s={displayedIso2s}
                            countries={displayedCountriesForBuyUrl}
                            variant="recommended"
                            lng={lng}
                            primary={false}
                            cardRole="default"
                            ctaVariant={unlim ? "default" : "basic"}
                            hideBanner={true}
                            noBorder={true}
                            hideTags={true}
                            hideSelectionChip={true}
                            isUpgradeNudge={unlim}
                            upgradePerDayCents={upgradePerDayCents}
                            selected={isSelected}
                            onSelect={() => selectPlanCard(key, plan, "single_country_flat")}
                          />

                          {/* Feature chips — only render capabilities this plan actually has */}
                          {(() => {
                            const activeChips = featureChips.filter((c) => c.on)
                            if (activeChips.length === 0) return null
                            return (
                              <div className="px-4 pb-3">
                                <div className="flex flex-wrap gap-1.5">
                                  {activeChips.map((c) => (
                                    <span
                                      key={c.key}
                                      className={cn(
                                        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium",
                                        c.bgOn,
                                        c.textOn
                                      )}
                                      aria-label={c.label}
                                      title={c.label}
                                    >
                                      {c.iconOn}
                                      {c.label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      )
                    })}
                  </div>

                  {/* "查看全部 N 个套餐" toggle — 比 Flutter 多一层方便 power user 对比同能力多 provider 价格。
                      hiddenCount > 0 才显示；展开后切换为"收起"。视觉做得明显（带边框 + 描边）让用户能注意到。*/}
                  {hiddenCount > 0 || showAllFlatPlans ? (
                    <div className="mt-5 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setShowAllFlatPlans((v) => !v)}
                        className={cn(
                          "inline-flex items-center gap-2 rounded-full border-2 border-orange-300 bg-white px-6 py-3 text-[14px] font-bold text-orange-700 transition",
                          "hover:bg-orange-50 hover:border-orange-400",
                          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                        )}
                        aria-expanded={showAllFlatPlans}
                      >
                        {showAllFlatPlans
                          ? t("recommendation-toggle-collapse", { count: ordered.length })
                          : t("recommendation-toggle-expand", { total: expandedOrdered.length, hidden: hiddenCount })}
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={cn("transition-transform", showAllFlatPlans && "rotate-180")}
                          aria-hidden
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </div>
                  ) : null}
                </div>
              )
            })()}

            {/* Multi-country: original quota-tier slot system — saver / fit / more / unlimited */}
            {displayedIso2s.length > 1 && (() => {
              // Build candidate pool covering all user iso2s.
              // Prefer smartCategories (already pre-categorized), fall back to dualResult.
              const userIso2sUpper = displayedIso2s.map((c) => c.toUpperCase())
              const coversAll = (plan: RecommendationPlan) =>
                userIso2sUpper.every((iso) =>
                  (plan.data.support_countries || []).map((c) => String(c).toUpperCase()).includes(iso)
                )

              let candidates: RecommendationPlan[] = []
              if (displayedSmartCategories) {
                candidates = dedupeSmartPlans([
                  ...displayedSmartCategories.single_country,
                  ...displayedSmartCategories.multi_country,
                  ...displayedSmartCategories.upgrades,
                ]).filter(coversAll)
              }
              if (candidates.length === 0) {
                const fromDual = [
                  ...displayedDualResult.exactMatches,
                  ...displayedDualResult.inventoryOffers,
                ].map((o) => o.plan)
                candidates = dedupeSmartPlans(fromDual).filter(coversAll)
              }

              const tierOffers = pickQuotaTierOffers({
                candidates,
                userTripQuotaMB,
              })

              if (tierOffers.length === 0) return null

              // Use the "fit" plan as the comparison anchor for saver/more/unlimited diffs.
              const fitOffer = tierOffers.find((t) => t.tier === "fit") || tierOffers[0]
              const isZh = (lng || "").toLowerCase().startsWith("zh")

              const tierStyle: Record<QuotaTier, {
                cardRole: "default" | "reference" | "featured"
                primary: boolean
              }> = {
                saver: { cardRole: "reference", primary: false },
                fit: { cardRole: "default", primary: true },
                more: { cardRole: "reference", primary: false },
                unlimited: { cardRole: "featured", primary: false },
              }

              const formatGB = (mb: number): string => {
                if (!isFinite(mb) || mb <= 0) return "0"
                const gb = mb / 1024
                return gb >= 10 ? Math.round(gb).toString() : (Math.round(gb * 10) / 10).toString()
              }

              return (
                <div className="mb-8">
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-home-orange_1" />
                    <h4 className="text-sm font-semibold text-stone-800">
                      {t("tier-recommended-title")}
                    </h4>
                    <span className="text-xs text-stone-600">
                      {t("tier-recommended-count", { count: tierOffers.length })}
                    </span>
                  </div>
                  <p className="mb-3 text-xs text-stone-600">
                    {t("tier-strip-help")}
                  </p>
                  <div
                    className={cn(
                      "grid grid-cols-1 gap-5",
                      tierOffers.length >= 4
                        ? "md:grid-cols-2 xl:grid-cols-4 xl:items-stretch"
                        : tierOffers.length === 3
                          ? "md:grid-cols-3 md:items-stretch"
                          : "md:grid-cols-2 md:items-stretch"
                    )}
                  >
                    {tierOffers.map((offer) => {
                      const cfg = tierStyle[offer.tier]
                      const key = planKey(offer.plan)
                      const priceDiffDollars = (offer.plan.amount - fitOffer.plan.amount) / 100
                      const quotaDiffMB = offer.tripQuotaMB - fitOffer.tripQuotaMB

                      // Banner title
                      const bannerTitle = t(`tier-banner-${offer.tier}`)

                      // Dynamic explanation with diff numbers
                      let explanation: string
                      if (offer.tier === "saver") {
                        const saved = Math.abs(priceDiffDollars).toFixed(2)
                        const lessGB = formatGB(Math.abs(quotaDiffMB))
                        explanation = t("tier-explanation-saver", { amount: saved, data: lessGB })
                      } else if (offer.tier === "fit") {
                        explanation = t("tier-explanation-fit")
                      } else if (offer.tier === "more") {
                        const extra = priceDiffDollars > 0 ? `+$${priceDiffDollars.toFixed(2)}` : `-$${Math.abs(priceDiffDollars).toFixed(2)}`
                        const extraGB = formatGB(Math.abs(quotaDiffMB))
                        explanation = t("tier-explanation-more", { data: extraGB, priceDiff: extra })
                      } else {
                        const extra = priceDiffDollars > 0 ? `+$${priceDiffDollars.toFixed(2)}` : `-$${Math.abs(priceDiffDollars).toFixed(2)}`
                        explanation = t("tier-explanation-unlimited", { priceDiff: extra })
                      }

                      return (
                        <div key={`tier_${offer.tier}_${key}`} className="flex flex-col">
                          <PlanCard
                            plan={{ ...offer.plan, alternativeProviders: [] }}
                            iso2s={displayedIso2s}
                            countries={displayedCountriesForBuyUrl}
                            variant="recommended"
                            lng={lng}
                            primary={cfg.primary}
                            cardRole={cfg.cardRole}
                            bannerTitle={bannerTitle}
                            offerExplanation={explanation}
                            isUpgradeNudge={offer.tier === "unlimited"}
                            upgradePerDayCents={
                              offer.tier === "unlimited" && offer.plan.data.day > 0
                                ? Math.round((offer.plan.amount - fitOffer.plan.amount) / offer.plan.data.day)
                                : undefined
                            }
                            selected={displayedSelectedPlanKey === key}
                            onSelect={() => selectPlanCard(key, offer.plan, "multi_country_tier")}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })()}

            {/* Tier 0.5: Near Matches — 最接近你的需求 */}
            {displayedDualResult.exactMatches.length === 0 && displayedDualResult.nearMatches.length > 0 && (
              <div className="mb-8">
                <div className="mb-2 flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-amber-500" />
                  <h4 className="text-sm font-semibold text-gray-800">
                    {t("tier-closest-title")}
                  </h4>
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-600">
                    {t("tier-closest-hint")}
                  </span>
                </div>
                <p className="mb-3 text-xs text-gray-400">
                  {t("tier-closest-description", { days })}
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3 items-stretch">
                  {displayedDualResult.nearMatches.map((offer, i) => {
                    const key = planKey(offer.plan)
                    return (
                      <PlanCard
                        key={`near_${offer.plan.provider}_${offer.plan.data.quota}_${offer.plan.data.day}_${i}`}
                        plan={{ ...offer.plan, alternativeProviders: [] }}
                        iso2s={displayedIso2s}
                        countries={displayedCountriesForBuyUrl}
                        variant="recommended"
                        lng={lng}
                        primary={offer.highlight}
                        offerExplanation={offer.explanation}
                        selected={displayedSelectedPlanKey === key}
                        onSelect={() => selectPlanCard(key, offer.plan, "near_match")}
                      />
                    )
                  })}
                </div>
              </div>
            )}
            <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-center">
              <p className="text-xs leading-relaxed text-gray-500">
                {t("results-focus-note")}
              </p>
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Country Picker Modal */}
      <CountryPickerModal
        show={showCountryPicker}
        onClose={() => setShowCountryPicker(false)}
        countries={availableCountries}
        selectedIso2s={selectedIso2s}
        onToggle={toggleCountry}
        t={t}
      />
    </section>
  )
}
